import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import * as RMenu from '@radix-ui/react-dropdown-menu';
import {
  Inbox, Send, FileText, Flag, MailOpen, Mail, Archive, Trash2, Reply, ReplyAll, Forward, FolderInput, ShieldAlert, Search, RefreshCw,
  Plus, Settings, ChevronDown, ChevronRight, ChevronLeft, Paperclip, CalendarPlus, CalendarCheck, MapPin, X, Image as ImageIcon,
  AlertTriangle, HeartHandshake, Wallet, Users, Download, Eye, Folder, PenSquare, Check, Loader2, AlarmClock, Tag, ListTodo, Printer, Ban,
  ShieldCheck, Languages, Sparkles, Clock4, MessagesSquare, AlarmClockOff, Pin, CalendarDays, UserCheck, UserX, StickyNote, Link2,
} from 'lucide-react';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { useModules, useFeatures } from '../../context/FeatureContext';
import { errMsg } from '../SupportCoordination/kit';
import Compose from './Compose';
import MailSettings from './MailSettings';
import Ribbon, { SnoozeDialog, ImmersiveReader } from './Ribbon';
import ScCalendarApp from '../SupportCoordination/calendar/ScCalendarApp';
import { AppointmentSheet, toForm, fromForm } from '../SupportCoordination/calendar/EventEditor';
import { fileHrefOf, LinkPicker } from '../SupportCoordination/calendar/InviteFields';
import { Modal } from '../SupportCoordination/kit';
import TasksApp from './TasksApp';
import MyDay from './MyDay';
import TeamPanel from './TeamPanel';
import Body from './MessageBody';
import { TagMenu, TagChips, RemindMenu, AiMenu, AiPanel, TaskDialog, MoreMenu, ThreadHeader, useSwipe, remindItems, aiItems } from './MessageTools';
import { EnsureLogTime, useLogTime } from '../SupportCoordination/workflow/LogTimeProvider';
import { AddAccountDialog, AccountSettingsDialog, startOAuth } from './AccountDialogs';
import { avatarColor, initials, who, firstOf, listTime, fullTime, groupOf, fmtSize, inviteTimes, inviteWhen } from './mailUi';
import './mail.css';

// Rostery Mail: every mailbox a person connects (Microsoft, Google or any IMAP mail), read and
// answered inside Rostery, laid out the way Outlook is: folders, the message list, the reading pane.
// Mail that involves a participant's contacts is tagged SC, PM or Client and links to their file.
// `module` opens it on one module's mail (the Support Coordination screen uses 'sc').

const VIEWS = {
  inbox: { label: 'Inbox', icon: Inbox },
  unread: { label: 'Unread', icon: Mail },
  flagged: { label: 'Flagged', icon: Flag },
  waiting: { label: 'Waiting for reply', icon: AlarmClock },
  snoozed: { label: 'Snoozed', icon: AlarmClockOff },
  // Shared mailboxes only (listed when the person can see one).
  assigned: { label: 'Assigned to me', icon: UserCheck, shared: true },
  unassigned: { label: 'Unassigned', icon: UserX, shared: true },
  sent: { label: 'Sent', icon: Send },
  drafts: { label: 'Drafts', icon: FileText },
};
const DEFAULT_PREFS = {
  appearance: { density: 'roomy', readingPane: 'right', preview: 1, avatars: true, fontSize: 'normal', zoom: 100, folderPane: true, ribbon: 'classic', myDay: false }, startup: 'inbox',
  read: { markRead: 'open', delaySec: 3, pictures: 'ask', confirmLinks: false }, conversations: true,
  toolbar: ['reply', 'replyAll', 'forward', 'archive', 'delete', 'move', 'junk', 'flag', 'unread', 'tag', 'remind', 'task', 'ai'],
  swipe: { left: 'archive', right: 'read' }, notifications: { desktop: false, sound: false },
  compose: { font: 'Segoe UI', fontSize: 14, alwaysCcBcc: false, defaultAccount: null }, send: { undoSeconds: 10, readReceipt: false },
  replies: { defaultReply: 'reply', includeOriginal: true }, signatures: {}, hiddenFolders: [], ai: { enabled: true, language: 'English' },
};
// Whatever the server sends, every section is there (an older server, or a section added later).
const withDefaults = (p) => { const o = { ...DEFAULT_PREFS }; for (const [k, v] of Object.entries(p || {})) o[k] = v && typeof v === 'object' && !Array.isArray(v) && DEFAULT_PREFS[k] && typeof DEFAULT_PREFS[k] === 'object' && !Array.isArray(DEFAULT_PREFS[k]) ? { ...DEFAULT_PREFS[k], ...v } : v; return o; };
const LAST_KEY = 'rostery-mail-last-view';
const selFromView = (v) => (['sc', 'pm', 'care'].includes(v) ? { module: v } : /^[0-9a-f-]{36}$/i.test(v) ? { folder_id: v } : { view: VIEWS[v] ? v : 'inbox' });
// A short chime for new mail (made in the browser; no sound file).
function chime() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext; const c = new Ctx(); const o = c.createOscillator(); const g = c.createGain();
    o.type = 'sine'; o.frequency.setValueAtTime(880, c.currentTime); o.frequency.setValueAtTime(1320, c.currentTime + 0.12);
    g.gain.setValueAtTime(0.0001, c.currentTime); g.gain.exponentialRampToValueAtTime(0.15, c.currentTime + 0.02); g.gain.exponentialRampToValueAtTime(0.0001, c.currentTime + 0.4);
    o.connect(g); g.connect(c.destination); o.start(); o.stop(c.currentTime + 0.42);
  } catch { /* no audio */ }
}
const MODULES = {
  sc: { label: 'Support Coordination', short: 'SC', icon: HeartHandshake, chip: 'bg-violet-50 text-violet-700 ring-violet-200/70', key: 'support_coordination' },
  pm: { label: 'Plan Management', short: 'PM', icon: Wallet, chip: 'bg-teal-50 text-teal-700 ring-teal-200/70', key: 'plan_management' },
  care: { label: 'Client contacts', short: 'Client', icon: Users, chip: 'bg-amber-50 text-amber-800 ring-amber-200/70', key: null },
};
const ROLE_ICON = { inbox: Inbox, sent: Send, drafts: FileText, archive: Archive, junk: ShieldAlert, trash: Trash2 };
const PAGE = 50;
// A touch screen (phone or tablet): swipe works there.
const TOUCH = typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia('(pointer: coarse)').matches;

const fileHref = (l) => (l.module === 'sc' && l.coordination_id ? `/support-coordination/${l.coordination_id}`
  : l.module === 'pm' ? `/plan-management/participants/${l.client_id}` : `/clients/${l.client_id}`);

function useIsPhone() {
  const q = '(max-width: 767px)';
  const [m, setM] = useState(() => typeof window !== 'undefined' && window.matchMedia(q).matches);
  useEffect(() => {
    const mq = window.matchMedia(q);
    const on = () => setM(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);
  return m;
}

export default function MailApp(props) {
  return <EnsureLogTime staff={[]} canLogForOthers={false}><MailInner {...props} /></EnsureLogTime>;
}

function MailInner({ module: startModule = null }) {
  const { user } = useAuth();
  const logTime = useLogTime();
  const modules = useModules() || {};
  const features = useFeatures() || {};
  const tasksOn = features.enable_tasks !== false;
  const [sp, setSp] = useSearchParams();
  const isPhone = useIsPhone();
  const [status, setStatus] = useState(null);
  const [accounts, setAccounts] = useState(null);
  const [sel, setSel] = useState(startModule ? { module: startModule } : { view: 'inbox' });
  const [filter, setFilter] = useState('all');
  const [q, setQ] = useState('');
  const [term, setTerm] = useState('');
  const [list, setList] = useState({ rows: [], total: 0, loading: true, error: null, more: false });
  const [openId, setOpenId] = useState(null);
  const [detail, setDetail] = useState({ loading: false, data: null, error: null });
  const [checked, setChecked] = useState(() => new Set());
  const [compose, setCompose] = useState(null);
  const [adding, setAdding] = useState(false);
  const [settingsFor, setSettingsFor] = useState(null);
  const [navOpen, setNavOpen] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [prefs, setPrefs] = useState(DEFAULT_PREFS);
  const [prefsReady, setPrefsReady] = useState(false);
  const [tags, setTags] = useState([]);
  const [settingsOpen, setSettingsOpen] = useState(null);
  const [thread, setThread] = useState([]);
  const [aiPanel, setAiPanel] = useState(null);
  const [taskFor, setTaskFor] = useState(null);
  const [quickSteps, setQuickSteps] = useState([]);
  const [undoStack, setUndoStack] = useState([]);
  const [immersive, setImmersive] = useState(false);
  const [snoozePick, setSnoozePick] = useState(false);
  const [ruleDraft, setRuleDraft] = useState(null);
  const [threadOpen, setThreadOpen] = useState(false);
  const [meeting, setMeeting] = useState(null);
  const [meetingSaving, setMeetingSaving] = useState(false);
  const navigate = useNavigate();
  const calendarView = sp.get('view') === 'calendar';
  const tasksView = sp.get('view') === 'tasks';
  const settingsView = sp.get('view') === 'settings';
  // Settings are a screen of their own (?view=settings), so the sidebar's Settings and Inbox work:
  // opening puts it in the address, and leaving the address (Inbox, back) closes them.
  const prevSettingsView = useRef(settingsView);
  useEffect(() => {
    if (settingsView) setSettingsOpen((x) => x || 'appearance');
    else if (prevSettingsView.current) setSettingsOpen(null);
    prevSettingsView.current = settingsView;
  }, [settingsView]);
  const openSettings = (section) => {
    setSettingsOpen(section || 'appearance');
    if (!settingsView) setSp((p) => { const n = new URLSearchParams(p); n.set('view', 'settings'); return n; });
  };
  const closeSettings = () => {
    setSettingsOpen(null);
    if (settingsView) setSp((p) => { const n = new URLSearchParams(p); n.delete('view'); return n; }, { replace: true });
  };
  // A link straight to one email (from a task, the flagged list or My day): open it.
  const openParam = sp.get('open');
  useEffect(() => {
    if (!openParam || !/^[0-9a-f-]{36}$/i.test(openParam)) return;
    setCompose(null); setSettingsOpen(null); setOpenId(openParam);
    setSp((p) => { const n = new URLSearchParams(p); n.delete('open'); return n; }, { replace: true });
  }, [openParam]); // the id in the address
  const composeParam = sp.get('compose');
  useEffect(() => {
    if (!composeParam || accounts === null) return;
    const to = sp.get('to'); const toName = sp.get('to_name') || '';
    setSp((p) => { const n = new URLSearchParams(p); ['compose', 'to', 'to_name', 'subject'].forEach((k) => n.delete(k)); return n; }, { replace: true });
    if (!(accounts || []).some((a) => a.can_act)) { toast.error('Add your own mailbox to send email from Rostery.'); return; }
    setSettingsOpen(null); setOpenId(null);
    setCompose({ mode: 'new', to: to && /@/.test(to) ? [{ name: toName, address: to }] : [], subject: sp.get('subject') || '' });
  }, [composeParam, accounts]); // the request in the address, once the mailboxes are known
  const lastUnread = useRef(null);
  const lastLocalAct = useRef(0);
  const seq = useRef(0);
  const searchRef = useRef(null);

  const loadTags = useCallback(() => api.get('/mail/tags').then((r) => setTags(r.data || [])).catch(() => {}), []);
  const loadQuickSteps = useCallback(() => api.get('/mail/quicksteps').then((r) => setQuickSteps(r.data || [])).catch(() => {}), []);
  // Each person's settings; a change saves at once (and shows at once).
  const savePrefs = useCallback(async (patch) => {
    setPrefs((p) => ({ ...p, ...patch }));
    try { const r = await api.put('/mail/prefs', patch); setPrefs(withDefaults(r.data)); } catch (e) { toast.error(errMsg(e)); }
  }, []);
  const loadAccounts = useCallback(() => api.get('/mail/accounts').then((r) => { setAccounts(r.data || []); return r.data || []; }).catch(() => { setAccounts((a) => a || []); return []; }), []);

  const params = useCallback((offset) => {
    const p = { limit: PAGE, offset };
    if (sel.folder_id) p.folder_id = sel.folder_id;
    else {
      if (sel.module) p.module = sel.module;
      if (sel.client_id) p.client_id = sel.client_id;
      if (sel.account_id) p.account_id = sel.account_id;
      if (sel.tag_id) p.tag_id = sel.tag_id;
      // A search looks through every folder (bar Deleted and Junk) unless a narrower view is open.
      if (sel.view && !(term && sel.view === 'inbox')) p.view = sel.view;
    }
    if (term) p.q = term;
    if (filter === 'unread') p.unread = 1;
    if (filter === 'flagged') p.flagged = 1;
    if (prefs.conversations && !['drafts', 'waiting', 'snoozed'].includes(sel.view)) p.threads = 1;
    return p;
  }, [sel, term, filter, prefs.conversations]);

  const loadList = useCallback(async ({ append = false, quiet = false } = {}) => {
    const my = ++seq.current;
    if (!quiet && !append) setList((l) => ({ ...l, loading: true, error: null }));
    try {
      const offset = append ? list.rows.length : 0;
      const r = await api.get('/mail/messages', { params: params(offset) });
      if (my !== seq.current) return;
      setList((l) => {
        const rows = append ? [...l.rows, ...r.data.rows.filter((x) => !l.rows.some((y) => y.id === x.id))] : r.data.rows;
        return { rows, total: r.data.total, loading: false, error: null, more: rows.length < r.data.total };
      });
    } catch (e) {
      if (my === seq.current) setList((l) => ({ ...l, loading: false, error: errMsg(e) }));
    }
  }, [params, list.rows.length]);

  // First load: settings, mailboxes, then a check of every mailbox in the background.
  useEffect(() => {
    api.get('/mail/prefs').then((r) => {
      setPrefs(withDefaults(r.data));
      if (!startModule) {
        let v = r.data.startup;
        if (v === 'last') { try { v = window.localStorage.getItem(LAST_KEY) || 'inbox'; } catch { v = 'inbox'; } }
        // A link straight to a view (the reminder email opens Waiting for reply) comes first.
        const asked = sp.get('view');
        if (asked && asked !== 'calendar' && (VIEWS[asked] || ['sc', 'pm', 'care'].includes(asked))) v = asked;
        if (v && v !== 'inbox') setSel(selFromView(v));
      }
    }).catch(() => {}).finally(() => setPrefsReady(true));
    loadTags(); loadQuickSteps();
    api.get('/mail/status').then((r) => setStatus(r.data)).catch(() => setStatus({ microsoft: false, google: false, presets: [] }));
    loadAccounts().then((a) => {
      if (!a.length) return;
      setSyncing(true);
      api.post('/mail/sync').then(() => { loadAccounts(); loadList({ quiet: true }); }).catch(() => {}).finally(() => setSyncing(false));
      setTimeout(() => { loadAccounts(); loadList({ quiet: true }); }, 30000);
    });
    // Coming back from signing in with Microsoft or Google.
    const connected = sp.get('connected'); const failed = sp.get('mail_error'); const calOk = sp.get('calendar_connected');
    if (calOk) {
      toast.success(`${calOk}: your Outlook calendar and Rostery are now kept in step. The first sync takes a minute.`, { duration: 7000 });
      setSp((p) => { const n = new URLSearchParams(p); n.delete('calendar_connected'); return n; }, { replace: true });
    }
    if (connected || failed) {
      if (connected) {
        toast.success(`${connected} is connected. Getting your mail…`);
        [5000, 15000, 30000, 60000, 120000].forEach((ms) => setTimeout(() => { loadAccounts(); loadList({ quiet: true }); }, ms));
      } else toast.error(failed);
      setSp((p) => { const n = new URLSearchParams(p); ['connected', 'account', 'mail_error'].forEach((k) => n.delete(k)); return n; }, { replace: true });
    }
  }, []); // once, on opening

  useEffect(() => { setTerm(''); setQ(''); }, [sel]);
  useEffect(() => { const t = setTimeout(() => setTerm(q.trim()), 300); return () => clearTimeout(t); }, [q]);
  useEffect(() => { if (prefsReady) { loadList(); setChecked(new Set()); } }, [sel, term, filter, prefs.conversations, prefsReady]); // a new list
  // "Where I left off": saved once the starting view is known (not before, or it would always be Inbox).
  useEffect(() => {
    if (!prefsReady) return;
    const v = sel.folder_id || sel.module || sel.view;
    if (v) { try { window.localStorage.setItem(LAST_KEY, v); } catch { /* storage blocked */ } }
  }, [sel, prefsReady]);
  // New mail: a notification and a chime when the Inbox count goes up (as the person chose).
  useEffect(() => {
    if (!accounts) return;
    const n = accounts.reduce((t, a) => t + a.folders.filter((f) => f.role === 'inbox').reduce((s, f) => s + (f.unread || 0), 0), 0);
    const before = lastUnread.current;
    lastUnread.current = n;
    if (before === null || n <= before) return;
    // Marking mail unread yourself (or undoing) is not new mail.
    if (Date.now() - lastLocalAct.current < 90000) return;
    if (prefs.notifications.sound) chime();
    if (prefs.notifications.desktop && 'Notification' in window && Notification.permission === 'granted' && document.visibilityState !== 'visible') {
      try { const note = new Notification('New mail in Rostery', { body: `${n - before} new message${n - before === 1 ? '' : 's'}`, tag: 'rostery-mail' }); note.onclick = () => window.focus(); } catch { /* blocked */ }
    }
  }, [accounts, prefs.notifications]);
  useEffect(() => {
    // In another tab, only the folder counts are checked (so "new mail" notifications can fire).
    const t = setInterval(() => { if (document.visibilityState === 'visible') { loadList({ quiet: true }); loadAccounts(); } else loadAccounts(); }, 60000);
    return () => clearInterval(t);
  }, [loadList, loadAccounts]);

  // The open message, whole.
  // The latest one asked for wins (a slow answer for a message clicked earlier is dropped), and a
  // mailbox that does not answer ends in a message with Try again, never an empty pane.
  const detailSeq = useRef(0);
  const fetchDetail = useCallback(async (id, { images = false } = {}) => {
    const my = ++detailSeq.current;
    setDetail((d) => ({ loading: true, data: images ? d.data : null, error: null }));
    try {
      const r = await api.get(`/mail/messages/${id}`, { params: images ? { images: 1, peek: 1 } : {}, timeout: 60000 });
      if (my !== detailSeq.current) return;
      setDetail({ loading: false, data: r.data, error: null });
      if (r.data.is_read) setList((l) => ({ ...l, rows: l.rows.map((x) => (x.id === id && !x.thread_count ? { ...x, is_read: true } : x)) }));
      loadAccounts();
    } catch (e) {
      if (my !== detailSeq.current) return;
      setDetail({ loading: false, data: null, error: e && e.code === 'ECONNABORTED' ? 'The mailbox is taking too long to send this message. Try again in a moment.' : errMsg(e) });
    }
  }, [loadAccounts]);
  useEffect(() => {
    setAiPanel(null); setThread([]); setThreadOpen(false);
    if (!openId) { setDetail({ loading: false, data: null, error: null }); return undefined; }
    fetchDetail(openId);
    api.get(`/mail/messages/${openId}/thread`).then((r) => setThread(r.data || [])).catch(() => {});
    // "Mark as read after a few seconds": only if it is still open then.
    if (prefs.read.markRead !== 'delay') return undefined;
    const t = setTimeout(() => {
      api.patch(`/mail/messages/${openId}`, { is_read: true }).then(() => {
        setList((l) => ({ ...l, rows: l.rows.map((x) => (x.id === openId ? { ...x, is_read: true } : x)) })); loadAccounts();
      }).catch(() => {});
    }, (prefs.read.delaySec || 3) * 1000);
    return () => clearTimeout(t);
  }, [openId, fetchDetail]); // the read rule as it was when the message opened

  const open = (id) => { setCompose(null); setOpenId(id); };
  const rows = list.rows;
  const idx = rows.findIndex((r) => r.id === openId);
  const step = (d) => { const n = rows[idx + d]; if (n) open(n.id); };

  // Undo, as in Outlook: the last few things done, each with the way back.
  const pushUndo = (label, fn) => setUndoStack((u) => [...u.slice(-9), { label, fn }]);
  const runUndo = async () => {
    const top = undoStack[undoStack.length - 1];
    if (!top) return;
    setUndoStack((u) => u.slice(0, -1));
    lastLocalAct.current = Date.now();
    try { await top.fn(); toast.success(`Undone: ${top.label}`); } catch (e) { toast.error(errMsg(e)); }
    loadList({ quiet: true }); loadAccounts();
  };
  const rowOf = (id) => rows.find((r) => r.id === id) || (detail.data && detail.data.id === id ? detail.data : null);
  // Moves back to where each message was (mail deleted from Deleted items is gone for good).
  const moveBack = (ids) => {
    const by = {};
    ids.map(rowOf).filter((r) => r && r.folder_id && r.folder_role !== 'trash').forEach((r) => { (by[r.folder_id] = by[r.folder_id] || []).push(r.id); });
    if (!Object.keys(by).length) return null;
    return async () => { for (const [fid, xs] of Object.entries(by)) await api.post('/mail/messages/bulk', { ids: xs, action: 'move', folder_id: fid }); };
  };
  const undoFor = (ids, action) => {
    if (['read', 'unread'].includes(action)) {
      const back = ids.filter((id) => { const r = rowOf(id); return r && r.is_read !== (action === 'read'); });
      return back.length ? () => api.post('/mail/messages/bulk', { ids: back, action: action === 'read' ? 'unread' : 'read' }) : null;
    }
    if (['flag', 'unflag'].includes(action)) {
      const back = ids.filter((id) => { const r = rowOf(id); return r && !!r.is_flagged !== (action === 'flag'); });
      return back.length ? () => api.post('/mail/messages/bulk', { ids: back, action: action === 'flag' ? 'unflag' : 'flag' }) : null;
    }
    return ['archive', 'delete', 'move', 'junk'].includes(action) ? moveBack(ids) : null;
  };
  const undoToast = (text) => toast((t) => (
    <span className="flex items-center gap-3 text-[13px]">{text}
      <button type="button" onClick={() => { toast.dismiss(t.id); runUndoRef.current(); }} className="font-semibold text-[#0f6cbd] hover:underline">Undo</button>
    </span>
  ), { duration: 6000 });
  const runUndoRef = useRef(null);
  runUndoRef.current = runUndo;

  // One action for one message or many. Done here at once; the mailbox follows.
  const act = async (ids, action, extra = {}) => {
    lastLocalAct.current = Date.now();
    const undo = undoFor(ids, action);
    const what = { archive: 'archived', delete: 'deleted', move: 'moved', junk: 'moved to Junk', read: 'marked read', unread: 'marked unread', flag: 'flagged', unflag: 'unflagged' }[action];
    const set = new Set(ids);
    const removes = ['archive', 'delete', 'move', 'junk'].includes(action);
    if (removes) {
      if (set.has(openId)) {
        const rest = rows.filter((r) => !set.has(r.id));
        const after = rows.slice(idx + 1).find((r) => !set.has(r.id)) || rest[rest.length - 1];
        setOpenId(isPhone ? null : (after ? after.id : null));
      }
      setList((l) => ({ ...l, rows: l.rows.filter((r) => !set.has(r.id)), total: Math.max(0, l.total - set.size) }));
    } else {
      const patch = { read: { is_read: true }, unread: { is_read: false }, flag: { is_flagged: true }, unflag: { is_flagged: false } }[action];
      setList((l) => ({ ...l, rows: l.rows.map((r) => (set.has(r.id) ? { ...r, ...patch } : r)) }));
      if (set.has(openId)) setDetail((d) => (d.data ? { ...d, data: { ...d.data, ...patch } } : d));
    }
    setChecked(new Set());
    try {
      const r = await api.post('/mail/messages/bulk', { ids, action, ...extra });
      if (r.data.failed) { toast.error(r.data.failed === ids.length ? (r.data.errors[0] && r.data.errors[0].error) || 'That did not work. Please try again.' : `${r.data.failed} of ${ids.length} could not be changed.`); loadList({ quiet: true }); }
      else {
        if (undo) pushUndo(`${ids.length === 1 ? 'message' : `${ids.length} messages`} ${what}`, undo);
        if (removes) {
          const text = `${ids.length === 1 ? 'Message' : `${ids.length} messages`} ${what}`;
          if (undo) undoToast(text); else toast.success(text);
        }
      }
      loadAccounts();
    } catch (e) { toast.error(errMsg(e)); loadList({ quiet: true }); }
  };

  // After Send: with undo send the message waits a few seconds (Undo reopens it); send later shows when.
  const afterSend = (info = {}) => {
    loadList({ quiet: true }); loadAccounts();
    if (!info.queued) return;
    const undo = async (t) => {
      toast.dismiss(t.id);
      try {
        await api.post(`/mail/outbox/${info.queued}/cancel`);
        setCompose({ ...info.restore, restore: info.restore });
        toast('Not sent. It is open again to change.');
      } catch (e) { toast.error(errMsg(e)); }
    };
    const whenText = new Date(info.send_at).toLocaleString('en-AU', { weekday: 'short', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' });
    toast((t) => (
      <span className="flex items-center gap-3 text-[13px]">
        {info.later ? `Scheduled for ${whenText}` : 'Sending…'}
        <button type="button" onClick={() => undo(t)} className="font-semibold text-[#0f6cbd] hover:underline">Undo</button>
      </span>
    ), { duration: info.later ? 8000 : Math.max(2000, new Date(info.send_at).getTime() - Date.now()) });
    if (!info.later) setTimeout(() => loadList({ quiet: true }), Math.max(0, new Date(info.send_at).getTime() - Date.now()) + 8000);
  };
  const reply = (mode) => {
    if (!detail.data) return;
    if (!mine.length) { toast.error('Only the owner of this mailbox can reply from it.'); return; }
    setCompose({ mode, source: detail.data, account_id: mine.some((x) => x.id === detail.data.account_id) ? detail.data.account_id : mine[0].id });
  };
  // The AI assistant: a summary or translation shows above the message; a drafted reply opens in Reply.
  const runAi = async (kind) => {
    if (!detail.data) return;
    if (!prefs.ai.enabled) { toast.error('The AI assistant is off in your mail settings.'); return; }
    const base = kind.startsWith('reply') ? 'reply' : kind;
    setAiPanel({ kind: base, loading: true });
    try {
      const body = base === 'translate' ? { action: 'translate', message_id: detail.data.id, language: prefs.ai.language }
        : base === 'reply' ? { action: 'reply', message_id: detail.data.id, tone: kind === 'reply-brief' ? 'brief' : 'professional' }
          : { action: 'summarise', message_id: detail.data.id };
      const r = await api.post('/mail/ai', body);
      setAiPanel({ kind: base, text: r.data.text });
    } catch (e) { setAiPanel({ kind: base, error: errMsg(e) }); }
  };
  const useAiReply = (text) => {
    const html = String(text).split(/\n{2,}/).map((p) => `<p>${p.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/\n/g, '<br>')}</p>`).join('');
    setAiPanel(null);
    if (!detail.data || !mine.length) return;
    setCompose({ mode: 'reply', source: detail.data, account_id: mine.some((x) => x.id === detail.data.account_id) ? detail.data.account_id : mine[0].id, body: html });
  };
  const blockSender = async (domain) => {
    if (!detail.data) return;
    const from = String(((detail.data.from || [])[0] || {}).address || '');
    if (!window.confirm(domain ? `Send all new mail from anyone at ${from.split('@')[1]} to Junk?` : `Send all new mail from ${from} to Junk?`)) return;
    try { await api.post(`/mail/messages/${detail.data.id}/block`, { domain }); toast.success('Blocked. Their mail goes to Junk.'); setOpenId(null); loadList({ quiet: true }); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const trustSender = async () => {
    if (!detail.data) return;
    try {
      await api.post(`/mail/messages/${detail.data.id}/trust`);
      if (prefs.read.pictures === 'ask') await savePrefs({ read: { ...prefs.read, pictures: 'safe' } });
      toast.success('Pictures from this sender will always show');
      fetchDetail(detail.data.id, { images: true });
    } catch (e) { toast.error(errMsg(e)); }
  };
  const logTimeFor = () => {
    const m = detail.data; if (!m) return;
    const sc = (m.links || []).find((l) => l.module === 'sc' && l.coordination_id);
    const from = (m.from || [])[0] || {};
    logTime({ coordinationId: sc ? sc.coordination_id : undefined, participantName: sc ? sc.label : undefined,
      defaults: { note_date: new Date(m.date || Date.now()).toISOString().slice(0, 10), summary: `Email ${m.folder_role === 'sent' ? 'to' : 'from'} ${from.name || from.address || ''}: ${m.subject || ''}`.slice(0, 500) } });
  };
  const printMessage = () => {
    const f = document.querySelector('iframe[title="Message"]');
    try { f && f.contentWindow && f.contentWindow.print(); } catch { window.print(); }
  };
  const mine = (accounts || []).filter((x) => x.can_act);
  const newMail = (extra = {}) => {
    if (!mine.length) { toast.error('You can read these mailboxes, but only their owners can send from them. Add your own mailbox to send.'); return; }
    const a = sel.account_id || (sel.folder_id && (accounts || []).find((x) => x.folders.some((f) => f.id === sel.folder_id))?.id);
    setCompose({ mode: 'new', account_id: mine.some((x) => x.id === a) ? a : undefined, ...extra });
    setNavOpen(false);
  };
  const sync = async () => {
    setSyncing(true);
    try {
      const target = sel.account_id || (sel.folder_id && (accounts || []).find((x) => x.folders.some((f) => f.id === sel.folder_id))?.id);
      await api.post(target ? `/mail/accounts/${target}/sync` : '/mail/sync');
      await Promise.all([loadAccounts(), loadList({ quiet: true })]);
    } catch (e) { toast.error(errMsg(e)); } finally { setSyncing(false); }
  };

  // Outlook's keys, with single letters as Outlook on the web allows.
  useEffect(() => {
    const k = (e) => {
      const t = e.target;
      if (compose || adding || settingsFor || settingsOpen || e.altKey) return;
      if (t && (t.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(t.tagName))) return;
      if (document.querySelector('[role="dialog"]')) return;
      if ((e.ctrlKey || e.metaKey) && e.key === 'z') { e.preventDefault(); runUndo(); return; }
      if (e.ctrlKey || e.metaKey) return;
      const cur = rows[idx];
      const go = (fn) => { e.preventDefault(); fn(); };
      if (e.key === 'n') go(() => newMail());
      else if (e.key === '/') go(() => searchRef.current && searchRef.current.focus());
      else if (e.key === 'ArrowDown' || e.key === 'j') go(() => (idx < 0 && rows[0] ? open(rows[0].id) : step(1)));
      else if (e.key === 'ArrowUp' || e.key === 'k') go(() => step(-1));
      else if (!cur) return;
      else if (e.key === 'r' && !e.shiftKey) go(() => reply('reply'));
      else if (e.key === 'a' || (e.key === 'R' && e.shiftKey)) go(() => reply('replyAll'));
      else if (e.key === 'f') go(() => reply('forward'));
      else if (e.key === 'e') go(() => act([cur.id], 'archive'));
      else if (e.key === 'Delete' || e.key === '#') go(() => act([cur.id], 'delete'));
      else if (e.key === 'u') go(() => act([cur.id], cur.is_read ? 'unread' : 'read'));
      else if (e.key === 'Insert' || e.key === 's') go(() => act([cur.id], cur.is_flagged ? 'unflag' : 'flag'));
      else if (e.key === 'Escape') go(() => setOpenId(null));
    };
    window.addEventListener('keydown', k);
    return () => window.removeEventListener('keydown', k);
  });

  // A new mailbox fills in the background: look again a few times while its mail arrives.
  const mailboxAdded = () => {
    loadAccounts().then(() => loadList());
    toast('Getting your mail. It appears here over the next minute.');
    [5000, 15000, 30000, 60000, 120000].forEach((ms) => setTimeout(() => { loadAccounts(); loadList({ quiet: true }); }, ms));
  };
  const accountOf = (id) => (accounts || []).find((a) => a.id === id);
  const folderInfo = useMemo(() => {
    if (!sel.folder_id) return null;
    for (const a of accounts || []) { const f = a.folders.find((x) => x.id === sel.folder_id); if (f) return { folder: f, account: a }; }
    return null;
  }, [sel, accounts]);
  const title = sel.folder_id ? (folderInfo ? folderInfo.folder.name : 'Folder')
    : sel.client_id || sel.tag_id ? sel.label || 'Participant'
      : sel.module ? MODULES[sel.module].label
        : (VIEWS[sel.view] || VIEWS.inbox).label;
  const subtitle = sel.folder_id && folderInfo ? folderInfo.account.email : sel.tag_id ? 'Tagged, from every mailbox' : sel.view === 'waiting' ? 'Waiting for a reply, and reminders' : sel.module || sel.client_id ? 'Mail with their contacts, from every mailbox' : (accounts || []).length > 1 ? 'All mailboxes' : (accounts && accounts[0] && accounts[0].email) || '';

  // The ribbon acts on the ticked messages, or else the open one.
  const targets = checked.size ? [...checked] : openId ? [openId] : [];
  const targetRows = targets.map(rowOf).filter(Boolean);
  const targetReadOnly = targetRows.some((r) => !(accountOf(r.account_id) || {}).can_act);
  const targetAccount = targetRows.length && targetRows.every((r) => r.account_id === targetRows[0].account_id) ? accountOf(targetRows[0].account_id) : null;
  const dropFromList = (ids) => {
    const set = new Set(ids);
    if (set.has(openId)) { const after = rows.slice(idx + 1).find((r) => !set.has(r.id)); setOpenId(isPhone ? null : (after ? after.id : null)); }
    setList((l) => ({ ...l, rows: l.rows.filter((r) => !set.has(r.id)), total: Math.max(0, l.total - set.size) }));
    setChecked(new Set());
  };
  const failNote = (r, n) => (r.data && r.data.failed ? toast.error(r.data.failed === n ? ((r.data.errors || [])[0] || {}).error || 'That did not work.' : `${r.data.failed} of ${n} could not be changed.`) : null);
  const ZOOMS = [80, 90, 100, 110, 125, 150];
  // Meeting: a new appointment with the people on the email invited, linked to the same file.
  const startMeeting = () => {
    const m = detail.data;
    if (!m) return;
    const own = new Set((accounts || []).map((a) => String(a.email || '').toLowerCase()));
    const seen = new Set();
    const people = [...(m.from || []), ...(m.reply_to || []), ...(m.to || []), ...(m.cc || [])].filter((a) => {
      const e = String((a && a.address) || '').toLowerCase();
      if (!e || own.has(e) || seen.has(e) || /no-?reply|mailer-daemon/.test(e)) return false;
      seen.add(e); return true;
    }).slice(0, 50).map((a) => ({ email: a.address.toLowerCase(), name: a.name || '', status: 'needs-action' }));
    const l = (m.links || [])[0];
    const link = !l ? null : l.module === 'sc' && l.coordination_id ? { module: 'sc', id: l.coordination_id, client_id: l.client_id, name: l.label }
      : { module: l.module === 'pm' ? 'pm' : 'care', id: l.client_id, client_id: l.client_id, name: l.label };
    const start = new Date(); start.setMinutes(start.getMinutes() < 30 ? 30 : 60, 0, 0);
    const end = new Date(start.getTime() + 3600000);
    const acc = mine.find((x) => x.id === m.account_id) || mine[0];
    const title = String(m.subject || '').replace(/^\s*((re|fw|fwd)\s*:\s*)+/i, '').slice(0, 200);
    setMeeting({ form: toForm({ start, end, allDay: false, title, coordinator_id: user && !user.impersonating ? String(user.id) : '' },
      { link, attendees: people, organizer_account_id: acc ? acc.id : '', source_message_id: m.id }) });
  };
  const saveMeeting = async () => {
    const r = fromForm(meeting.form, 'email');
    if (r.error) { toast.error(r.error); return; }
    setMeetingSaving(true);
    try {
      const res = await api.post('/calendar/appointments', r.body);
      const inv = res.data && res.data.invites;
      if (inv && inv.error) toast.error(inv.error);
      const day = r.start;
      const ymdOf = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      toast((t) => (
        <span className="flex items-center gap-3 text-[13px]">
          {inv && inv.sent ? `Meeting booked. Invites sent to ${inv.sent} ${inv.sent === 1 ? 'person' : 'people'}.` : 'Meeting booked.'}
          <button type="button" className="font-semibold text-[#0f6cbd] hover:underline" onClick={() => { toast.dismiss(t.id); navigate(`/mail?view=calendar&cv=day&d=${ymdOf(day)}`); }}>Open calendar</button>
        </span>
      ), { duration: 7000 });
      setMeeting(null);
    } catch (e) { toast.error(errMsg(e)); } finally { setMeetingSaving(false); }
  };
  const ribbonOn = {
    new: () => newMail(),
    ignore: async () => {
      if (!detail.data) return;
      if (!window.confirm('Ignore this conversation? It and every new reply in it go to Deleted items.')) return;
      try {
        const r = await api.post(`/mail/messages/${detail.data.id}/ignore`, {});
        const id = detail.data.id;
        pushUndo('ignore conversation', () => api.post(`/mail/messages/${id}/ignore`, { on: false }));
        toast.success(`Ignored. ${r.data.moved || 0} message${r.data.moved === 1 ? '' : 's'} moved to Deleted items.`);
        setOpenId(null); loadList({ quiet: true }); loadAccounts();
      } catch (e) { toast.error(errMsg(e)); }
    },
    block: () => blockSender(false), blockDomain: () => blockSender(true), trust: trustSender,
    del: () => act(targets, 'delete'), archive: () => act(targets, 'archive'),
    report: async (kind) => {
      const ids = targets; const undo = moveBack(ids);
      dropFromList(ids);
      try {
        const r = await api.post('/mail/messages/report', { ids, kind });
        if (!failNote(r, ids.length)) {
          if (undo) pushUndo(kind === 'phishing' ? 'report phishing' : 'report junk', undo);
          toast.success(`Moved to Junk${(r.data.blocked || []).length ? `, and ${r.data.blocked.join(', ')} blocked` : ''}.${kind === 'phishing' ? ' Never click links or open files in mail like this.' : ''}`);
        }
      } catch (e) { toast.error(errMsg(e)); }
      loadList({ quiet: true }); loadAccounts();
    },
    reply: () => reply('reply'), replyAll: () => reply('replyAll'), forward: () => reply('forward'),
    sweep: async ({ mode, action, always }) => {
      if (!detail.data) return;
      const from = String(((detail.data.from || [])[0] || {}).address || 'this sender');
      const say = { all: `every message from ${from}`, keep_latest: `every message from ${from} but the newest`, older_10: `messages from ${from} older than 10 days` }[mode];
      if (!window.confirm(`${action === 'archive' ? 'Archive' : 'Delete'} ${say} in the Inbox${always ? ', and do the same to their new mail from now on' : ''}?`)) return;
      try {
        const r = await api.post(`/mail/messages/${detail.data.id}/sweep`, { mode, action, always });
        toast.success(`${r.data.done || 0} message${r.data.done === 1 ? '' : 's'} ${action === 'archive' ? 'archived' : 'deleted'}${always && !r.data.ruleError ? '. A rule now does this to their new mail.' : ''}`);
        if (r.data.ruleError) toast.error(r.data.ruleError);
        setOpenId(null); loadList({ quiet: true }); loadAccounts();
      } catch (e) { toast.error(errMsg(e)); }
    },
    move: (f) => act(targets, 'move', { folder_id: f.id }),
    rules: () => { setRuleDraft(null); openSettings('rules'); },
    newRule: () => {
      const from = detail.data && String(((detail.data.from || [])[0] || {}).address || '');
      setRuleDraft(from ? { name: `Mail from ${from}`.slice(0, 80), conditions: { from }, actions: {} } : { name: '', conditions: {}, actions: {} });
      openSettings('rules');
    },
    quickStep: async (s) => {
      const ids = targets;
      try {
        const r = await api.post(`/mail/quicksteps/${s.id}/run`, { ids });
        if (!failNote(r, ids.length)) toast.success(`${s.name}: done`);
        setChecked(new Set()); loadList({ quiet: true }); loadAccounts();
        if (openId && ids.includes(openId)) fetchDetail(openId);
      } catch (e) { toast.error(errMsg(e)); }
    },
    manageQuickSteps: () => openSettings('quicksteps'),
    read: () => act(targets, 'read'), unread: () => act(targets, 'unread'), flag: () => act(targets, 'flag'), unflag: () => act(targets, 'unflag'),
    tag: async (t) => {
      const ids = targets;
      const on = !(detail.data && ids.length === 1 && ids[0] === detail.data.id && (detail.data.tag_ids || []).includes(t.id));
      try {
        await api.post('/mail/messages/tag', { ids, tag_id: t.id, on });
        pushUndo(`${on ? 'tag' : 'untag'} ${t.name}`, () => api.post('/mail/messages/tag', { ids, tag_id: t.id, on: !on }));
        if (detail.data && ids.includes(detail.data.id)) setDetail((d) => (d.data ? { ...d, data: { ...d.data, tag_ids: on ? [...new Set([...(d.data.tag_ids || []), t.id])] : (d.data.tag_ids || []).filter((x) => x !== t.id) } } : d));
        loadList({ quiet: true });
      } catch (e) { toast.error(errMsg(e)); }
    },
    manageTags: () => openSettings('tags'),
    pin: () => ribbonOn.setPin(true), unpin: () => ribbonOn.setPin(false),
    setPin: async (on) => {
      const ids = targets;
      setList((l) => ({ ...l, rows: l.rows.map((r) => (ids.includes(r.id) ? { ...r, pinned_at: on ? new Date().toISOString() : null } : r)) }));
      if (detail.data && ids.includes(detail.data.id)) setDetail((d) => ({ ...d, data: { ...d.data, pinned_at: on ? new Date().toISOString() : null } }));
      try {
        const r = await api.post('/mail/messages/pin', { ids, on });
        if (!failNote(r, ids.length)) pushUndo(on ? 'pin' : 'unpin', () => api.post('/mail/messages/pin', { ids, on: !on }));
      } catch (e) { toast.error(errMsg(e)); }
      setChecked(new Set()); loadList({ quiet: true });
    },
    snooze: async (when) => {
      if (when === 'pick') { setSnoozePick(true); return; }
      const ids = targets;
      setSnoozePick(false);
      dropFromList(ids);
      try {
        const r = await api.post('/mail/messages/snooze', { ids, until: when.toISOString() });
        if (!failNote(r, ids.length)) {
          pushUndo('snooze', () => api.post('/mail/messages/snooze', { ids, until: null }));
          undoToast(`Snoozed until ${when.toLocaleString('en-AU', { weekday: 'short', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' })}. It is under Snoozed until then.`);
        }
      } catch (e) { toast.error(errMsg(e)); }
      loadList({ quiet: true });
    },
    print: printMessage, undo: runUndo,
    settings: () => openSettings('appearance'),
    collapse: () => setThreadOpen((v) => !v),
    zoom: (d) => {
      const z = prefs.appearance.zoom || 100;
      const i = ZOOMS.indexOf(z) < 0 ? 2 : ZOOMS.indexOf(z);
      savePrefs({ appearance: { ...prefs.appearance, zoom: ZOOMS[Math.max(0, Math.min(ZOOMS.length - 1, i + d))] } });
    },
    sync, immersive: () => setImmersive(true), meeting: startMeeting,
  };
  const showMail = () => setSp((p) => { const n = new URLSearchParams(p); ['view', 'cv', 'd'].forEach((k) => n.delete(k)); return n; });
  const showCalendar = () => setSp((p) => { const n = new URLSearchParams(p); n.set('view', 'calendar'); return n; });
  const showTasks = () => setSp((p) => { const n = new URLSearchParams(p); n.set('view', 'tasks'); ['cv', 'd'].forEach((k) => n.delete(k)); return n; });
  const switcher = <AppSwitch current={calendarView ? 'calendar' : tasksView ? 'tasks' : 'mail'} onMail={showMail} onCalendar={showCalendar} onTasks={showTasks} />;

  // Tasks: office and SC tasks in one list, with flagged email.
  if (tasksView) return <TasksApp sideTop={switcher} />;

  // The calendar: every appointment in the company, linked to SC, PM or client files.
  if (calendarView) {
    return (
      <ScCalendarApp scope="email" staff={[]} sideTop={switcher} canLogTime={modules.support_coordination !== false}
        onOpenFile={(coordinationId, occ) => {
          const href = coordinationId ? `/support-coordination/${coordinationId}` : occ && occ.client_id ? fileHrefOf({ module: occ.module, id: occ.client_id, client_id: occ.client_id }) : null;
          if (href) navigate(href);
        }} />
    );
  }

  // Nothing connected yet: the welcome.
  if (accounts && !accounts.length) {
    return (
      <Shell>
        <Welcome onAdd={() => setAdding(true)} status={status} />
        <AddAccountDialog open={adding} onClose={() => setAdding(false)} status={status} onAdded={mailboxAdded} />
      </Shell>
    );
  }

  const pane = prefs.appearance.readingPane;
  const single = isPhone || pane === 'off';
  const showReading = !single || !!openId || !!compose;
  const showList = !single || (!openId && !compose);
  const nav = (
    <NavPane accounts={accounts} sel={sel} onSelect={(s) => { setSel(s); setOpenId(null); setNavOpen(false); closeSettings(); }} modules={modules}
      onNew={() => newMail()} onAdd={() => { setAdding(true); setNavOpen(false); }} onSettings={(a) => setSettingsFor(a)}
      hidden={prefs.hiddenFolders} tags={tags} onOpenSettings={() => { openSettings('appearance'); setNavOpen(false); }} switcher={switcher} />
  );
  const aiAllowed = modules.ai_features === true || (user && user.role === 'super_admin');
  const paneP = aiAllowed ? prefs : { ...prefs, ai: { ...prefs.ai, enabled: false } };
  const fontCls = prefs.appearance.fontSize === 'small' ? 'mail-font-small' : prefs.appearance.fontSize === 'large' ? 'mail-font-large' : '';
  const folderPane = prefs.appearance.folderPane !== false;
  const showRibbon = !isPhone;

  return (
    <Shell>
      {/* Folders: always there on a wide screen, a drawer on smaller ones. */}
      <aside className={clsx('hidden w-[236px] flex-shrink-0 flex-col border-r border-slate-200 bg-[#fafafa]', folderPane && 'lg:flex')}>{nav}</aside>
      {navOpen && (
        <div className={clsx('absolute inset-0 z-30 flex', folderPane && 'lg:hidden')}>
          <div className="w-[272px] max-w-[85%] h-full bg-[#fafafa] shadow-2xl flex flex-col animate-slide-in-left">{nav}</div>
          <button type="button" aria-label="Close folders" className="flex-1 bg-slate-900/25" onClick={() => setNavOpen(false)} />
        </div>
      )}

      {settingsOpen ? (
        <MailSettings aiAllowed={aiAllowed} prefs={prefs} savePrefs={savePrefs} accounts={accounts} me={user} tags={tags} reloadTags={loadTags} reloadAccounts={loadAccounts}
          startAt={settingsOpen === true ? null : settingsOpen} onClose={() => {
            closeSettings(); setRuleDraft(null); loadQuickSteps();
          }} onAdd={() => setAdding(true)} onAccountSettings={(a) => setSettingsFor(a)}
          onCompose={(extra) => { closeSettings(); newMail(extra); }} onOpenMessage={(id) => { closeSettings(); setSel({ view: 'waiting' }); open(id); }}
          ruleDraft={ruleDraft} onQuickStepsChanged={loadQuickSteps}
          scOn={modules.support_coordination !== false} pmOn={modules.plan_management !== false} tasksOn={tasksOn} />
      ) : <div className="flex flex-col flex-1 min-w-0 min-h-0">
      {showRibbon && (
        <Ribbon prefs={prefs} savePrefs={savePrefs} has={targets.length > 0} readOnly={targetReadOnly} msg={detail.data && detail.data.id === openId ? detail.data : null}
          tags={tags} accountFolders={targetAccount ? targetAccount.folders : []} quickSteps={quickSteps} undoLabel={undoStack.length ? undoStack[undoStack.length - 1].label : null}
          threadOpen={threadOpen} hasThread={thread.length > 1} on={ribbonOn} />
      )}
      <div className="flex flex-1 min-w-0 min-h-0">
      <div className={clsx('flex flex-1 min-w-0 min-h-0', pane === 'bottom' && !isPhone && 'flex-col', fontCls)}>
      {showList && (
        <section className={clsx('flex flex-col min-w-0 bg-white', single ? 'flex-1' : pane === 'bottom' ? 'h-[42%] min-h-[220px] border-b border-slate-200' : 'w-[340px] xl:w-[380px] flex-shrink-0 border-r border-slate-200')} aria-label="Messages">
          <ListHeader title={title} subtitle={subtitle} q={q} setQ={setQ} searchRef={searchRef} filter={filter} setFilter={setFilter}
            syncing={syncing} onSync={sync} onMenu={() => setNavOpen(true)} navHidden={!folderPane} onNew={() => newMail()} total={list.total}
            checked={checked} rows={rows} setChecked={setChecked} onBulk={(action, extra) => act([...checked], action, extra)} accounts={accounts}
            onClearClient={sel.client_id || sel.tag_id ? () => setSel({ view: 'inbox' }) : null} />
          <MessageList list={list} openId={openId} onOpen={open} checked={checked} setChecked={setChecked} onAct={act}
            multi={(accounts || []).length > 1} accountOf={accountOf} onMore={() => loadList({ append: true })} onRetry={() => loadList()} term={term}
            prefs={prefs} tags={tags} isPhone={isPhone} />
        </section>
      )}

      {showReading && (
        <section className="flex-1 min-w-0 min-h-0 flex flex-col bg-white" aria-label={compose ? 'New message' : 'Reading pane'}>
          {compose ? (
            <Compose key={`${compose.mode}-${compose.source ? compose.source.id : 'new'}`} accounts={mine} start={compose} prefs={prefs} aiOn={paneP.ai.enabled}
              onClose={() => setCompose(null)} onSent={afterSend} />
          ) : openId ? (
            <ReadingPane detail={detail} row={rows[idx]} isPhone={single} onBack={() => setOpenId(null)} onReply={reply} onAct={(a, extra) => act([openId], a, extra)}
              account={detail.data && accountOf(detail.data.account_id)} onShowImages={() => fetchDetail(openId, { images: true })}
              onRetry={() => fetchDetail(openId)} canCalendar={!!(user && ['admin', 'office_user', 'super_admin'].includes(user.role))}
              onFilterClient={(l) => { setSel({ client_id: l.client_id, label: l.label }); }} onPrev={idx > 0 ? () => step(-1) : null} onNext={idx >= 0 && idx < rows.length - 1 ? () => step(1) : null}
              prefs={paneP} tags={tags} thread={thread} onOpenThread={(id) => setOpenId(id)} aiPanel={aiPanel} onAi={runAi} onCloseAi={() => setAiPanel(null)} onUseAiReply={useAiReply}
              onTagged={(tagId, on) => { setDetail((d) => (d.data ? { ...d, data: { ...d.data, tag_ids: on ? [...(d.data.tag_ids || []), tagId] : (d.data.tag_ids || []).filter((x) => x !== tagId) } } : d)); loadList({ quiet: true }); }}
              onManageTags={() => openSettings('tags')} onTask={() => setTaskFor(detail.data)} onLogTime={logTimeFor} onPrint={printMessage}
              onBlock={blockSender} onTrust={trustSender} onWatched={() => loadList({ quiet: true })} canLogTime={modules.support_coordination !== false} canTask={tasksOn}
              ribbon={showRibbon} zoom={prefs.appearance.zoom || 100} threadOpen={threadOpen} onToggleThread={() => setThreadOpen((v) => !v)} />
          ) : (
            <NothingOpen count={list.total} />
          )}
        </section>
      )}
      </div>
      {prefs.appearance.myDay && !isPhone && <MyDay onClose={() => savePrefs({ appearance: { ...prefs.appearance, myDay: false } })} />}
      </div>
      </div>}
      <TaskDialog open={!!taskFor} message={taskFor} onClose={() => setTaskFor(null)} />
      <AppointmentSheet open={!!meeting} editing={null} form={meeting && meeting.form} onForm={(form) => setMeeting((x) => ({ ...x, form }))}
        onClose={() => setMeeting(null)} onSave={saveMeeting} saving={meetingSaving} scope="email"
        me={{ userId: user && user.id }} coordinators={user ? [{ id: user.id, first_name: user.first_name || user.firstName, last_name: user.last_name || user.lastName }] : []} staff={[]} occs={[]} />
      <SnoozeDialog open={snoozePick} onClose={() => setSnoozePick(false)} onPick={(d) => ribbonOn.snooze(d)} />
      <ImmersiveReader open={immersive} onClose={() => setImmersive(false)} message={detail.data} />

      <AddAccountDialog open={adding} onClose={() => setAdding(false)} status={status} onAdded={mailboxAdded} />
      <AccountSettingsDialog account={settingsFor} me={user} onClose={() => setSettingsFor(null)} onSaved={loadAccounts}
        onRemoved={() => { setSel({ view: 'inbox' }); setOpenId(null); loadAccounts(); loadList(); }} />
    </Shell>
  );
}

function Shell({ children }) {
  return <div className="relative flex h-[calc(100dvh-7rem)] min-h-[560px] bg-white rounded-2xl border border-slate-200/80 shadow-card overflow-hidden text-slate-900">{children}</div>;
}

// ── Folders ─────────────────────────────────────────────────────────────────────────────────────
// Mail or Calendar, at the top of the folder pane and the calendar's sidebar.
function AppSwitch({ current, onMail, onCalendar, onTasks }) {
  return (
    <div className="flex items-center gap-0.5 rounded-lg bg-slate-200/60 p-1" role="tablist" aria-label="Email">
      {[['mail', 'Mail', Mail, onMail], ['calendar', 'Calendar', CalendarDays, onCalendar], ['tasks', 'Tasks', ListTodo, onTasks]].map(([k, l, Icon, go]) => (
        <button key={k} type="button" role="tab" aria-selected={current === k} onClick={current === k ? undefined : go}
          className={clsx('flex-1 inline-flex items-center justify-center gap-1 h-8 rounded-md text-[12.5px] font-semibold transition-colors',
            current === k ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900')}>
          <Icon size={14} className={current === k ? 'text-[#0f6cbd]' : ''} aria-hidden />{l}
        </button>
      ))}
    </div>
  );
}
function NavPane({ accounts, sel, onSelect, modules, onNew, onAdd, onSettings, hidden = [], tags = [], onOpenSettings, switcher = null }) {
  const [closed, setClosed] = useState({});
  const hide = new Set(hidden);
  const inboxUnread = (accounts || []).reduce((t, a) => t + a.folders.filter((f) => f.role === 'inbox').reduce((s, f) => s + (f.unread || 0), 0), 0);
  const draftCount = (accounts || []).reduce((t, a) => t + a.folders.filter((f) => f.role === 'drafts').reduce((s, f) => s + (f.total || 0), 0), 0);
  const mods = Object.entries(MODULES).filter(([, m]) => !m.key || modules[m.key] !== false);
  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="p-3 space-y-3">
        {switcher}
        <button type="button" onClick={onNew}
          className="w-full inline-flex items-center justify-center gap-2 h-10 rounded-lg bg-[#0f6cbd] text-white text-[14px] font-semibold shadow-sm hover:bg-[#115ea3] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#0f6cbd]">
          <PenSquare size={16} aria-hidden /> New mail
        </button>
      </div>
      <nav className="flex-1 min-h-0 overflow-y-auto mail-scroll px-2 pb-3" aria-label="Mail folders">
        <NavGroup label="Favourites">
          {Object.entries(VIEWS).filter(([, v]) => !v.shared || (accounts || []).some((a) => a.visibility !== 'owner')).map(([k, v]) => (
            <NavRow key={k} icon={v.icon} label={v.label} active={!sel.folder_id && !sel.module && !sel.client_id && sel.view === k}
              count={k === 'inbox' ? inboxUnread : k === 'drafts' ? draftCount : 0} muted={k === 'drafts'} onClick={() => onSelect({ view: k })} />
          ))}
        </NavGroup>
        <NavGroup label="By participant">
          {mods.map(([k, m]) => <NavRow key={k} icon={m.icon} label={m.label} active={sel.module === k && !sel.client_id} onClick={() => onSelect({ module: k })} />)}
        </NavGroup>
        {tags.length > 0 && (
          <NavGroup label="Tags">
            {tags.map((t) => (
              <NavRow key={t.id} icon={({ size }) => <span className="inline-flex items-center justify-center" style={{ width: size, height: size }}><span className="w-2.5 h-2.5 rounded-full" style={{ background: t.color }} /></span>}
                label={t.name} active={sel.tag_id === t.id} onClick={() => onSelect({ tag_id: t.id, label: t.name })} />
            ))}
          </NavGroup>
        )}
        {(accounts || []).map((a) => {
          const isClosed = closed[a.id];
          const shown = a.folders.filter((f) => !hide.has(f.id));
          const top = shown.filter((f) => !f.parent_remote_id || !shown.some((p) => p.remote_id === f.parent_remote_id));
          return (
            <div key={a.id} className="mt-3">
              <div className="group flex items-center gap-1 pr-1">
                <button type="button" onClick={() => setClosed((c) => ({ ...c, [a.id]: !isClosed }))} aria-expanded={!isClosed}
                  className="flex-1 min-w-0 flex items-center gap-1.5 px-1.5 py-1.5 rounded-md text-left hover:bg-slate-200/50">
                  {isClosed ? <ChevronRight size={14} className="text-slate-500 flex-shrink-0" aria-hidden /> : <ChevronDown size={14} className="text-slate-500 flex-shrink-0" aria-hidden />}
                  <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: a.color || avatarColor(a.email) }} aria-hidden />
                  <span className="truncate text-[13px] font-semibold text-slate-800" title={a.email}>{a.display_name || a.email}</span>
                  {a.status !== 'active' && <AlertTriangle size={13} className="text-amber-600 flex-shrink-0" aria-label="Needs attention" />}
                </button>
                <button type="button" onClick={() => onSettings(a)} aria-label={`Settings or sign out for ${a.email}`} title="Settings and sign out"
                  className="w-7 h-7 rounded-md flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-200/60 lg:opacity-0 lg:group-hover:opacity-100 focus:opacity-100">
                  <Settings size={14} aria-hidden />
                </button>
              </div>
              {!isClosed && (
                <div className="mt-0.5">
                  {a.status === 'reauth' && a.provider !== 'imap' && (
                    <button type="button" onClick={() => startOAuth(a.provider, a.visibility).catch((e) => toast.error(errMsg(e)))}
                      className="w-full text-left mx-0 mb-1 px-2.5 py-1.5 rounded-md bg-amber-50 text-[12px] text-amber-900 hover:bg-amber-100">Sign in again to keep this mailbox up to date</button>
                  )}
                  <FolderTree folders={shown} items={top} depth={0} sel={sel} onSelect={(f) => onSelect({ folder_id: f.id })} />
                </div>
              )}
            </div>
          );
        })}
      </nav>
      <div className="p-2 border-t border-slate-200 flex gap-1">
        <button type="button" onClick={onAdd} className="flex-1 flex items-center gap-2 px-2.5 py-2 rounded-md text-[13px] text-[#0f6cbd] font-semibold hover:bg-sky-50">
          <Plus size={15} aria-hidden /> Add mailbox
        </button>
        <button type="button" onClick={onOpenSettings} className="flex items-center gap-1.5 px-2.5 py-2 rounded-md text-[13px] text-slate-700 font-semibold hover:bg-slate-200/50" title="Mail settings">
          <Settings size={15} aria-hidden /> Settings
        </button>
      </div>
    </div>
  );
}
function FolderTree({ folders, items, depth, sel, onSelect }) {
  return items.map((f) => {
    const kids = folders.filter((c) => c.parent_remote_id && c.parent_remote_id === f.remote_id);
    return (
      <React.Fragment key={f.id}>
        <NavRow icon={ROLE_ICON[f.role] || Folder} label={f.name} depth={depth} active={sel.folder_id === f.id}
          count={f.role === 'drafts' ? f.total : ['sent', 'trash', 'junk', 'archive'].includes(f.role) ? 0 : f.unread} muted={f.role === 'drafts'} onClick={() => onSelect(f)} />
        {kids.length > 0 && depth < 4 && <FolderTree folders={folders} items={kids} depth={depth + 1} sel={sel} onSelect={onSelect} />}
      </React.Fragment>
    );
  });
}
function NavGroup({ label, children }) {
  return (
    <div className="mt-1.5">
      <p className="px-2 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wider text-slate-500">{label}</p>
      {children}
    </div>
  );
}
function NavRow({ icon: Icon, label, active, count, muted, onClick, depth = 0 }) {
  return (
    <button type="button" onClick={onClick} aria-current={active ? 'page' : undefined}
      className={clsx('relative w-full flex items-center gap-2.5 py-1.5 pr-2 rounded-md text-[13.5px] text-left transition-colors',
        active ? 'bg-sky-100/80 text-slate-900 font-semibold' : 'text-slate-700 hover:bg-slate-200/50')}
      style={{ paddingLeft: 10 + depth * 14 }}>
      {active && <span className="absolute left-0 top-1.5 bottom-1.5 w-[3px] rounded-full bg-[#0f6cbd]" aria-hidden />}
      <Icon size={16} className={active ? 'text-[#0f6cbd]' : 'text-slate-500'} aria-hidden />
      <span className="flex-1 truncate">{label}</span>
      {count > 0 && <span className={clsx('text-[12px] tabular-nums', muted ? 'text-slate-500' : 'font-semibold text-[#0f6cbd]')}>{count > 999 ? '999+' : count}</span>}
    </button>
  );
}

// ── The list ────────────────────────────────────────────────────────────────────────────────────
function ListHeader({ title, subtitle, q, setQ, searchRef, filter, setFilter, syncing, onSync, onMenu, navHidden, onNew, total, checked, rows, setChecked, onBulk, accounts, onClearClient }) {
  const n = checked.size;
  const picked = rows.filter((r) => checked.has(r.id));
  const oneAccount = picked.length && picked.every((r) => r.account_id === picked[0].account_id) ? (accounts || []).find((a) => a.id === picked[0].account_id) : null;
  return (
    <div className="flex-shrink-0 border-b border-slate-200">
      <div className="flex items-center gap-2 px-3 pt-3">
        <button type="button" onClick={onMenu} className={clsx(!navHidden && 'lg:hidden', 'w-9 h-9 rounded-md flex items-center justify-center text-slate-600 hover:bg-slate-100')} aria-label="Folders"><Folder size={18} /></button>
        <div className="relative flex-1">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" aria-hidden />
          <input ref={searchRef} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search mail" aria-label="Search mail"
            className="w-full h-9 pl-9 pr-8 rounded-md bg-slate-100 text-[13.5px] placeholder:text-slate-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#0f6cbd]/60" />
          {q && <button type="button" onClick={() => setQ('')} aria-label="Clear search" className="absolute right-1.5 top-1/2 -translate-y-1/2 w-6 h-6 rounded flex items-center justify-center text-slate-400 hover:text-slate-700"><X size={14} /></button>}
        </div>
        <button type="button" onClick={onNew} className="lg:hidden w-9 h-9 rounded-md flex items-center justify-center bg-[#0f6cbd] text-white" aria-label="New mail"><PenSquare size={16} /></button>
      </div>
      {n > 0 ? (
        <div className="flex items-center gap-1 px-3 py-2 bg-sky-50/70 mt-2">
          <button type="button" onClick={() => setChecked(new Set())} className="w-8 h-8 rounded-md flex items-center justify-center text-slate-600 hover:bg-white" aria-label="Clear selection"><X size={15} /></button>
          <span className="text-[13px] font-semibold text-slate-800 mr-auto">{n} selected</span>
          <IconBtn label="Mark as read" icon={MailOpen} onClick={() => onBulk('read')} />
          <IconBtn label="Mark as unread" icon={Mail} onClick={() => onBulk('unread')} />
          <IconBtn label="Flag" icon={Flag} onClick={() => onBulk('flag')} />
          <IconBtn label="Archive" icon={Archive} onClick={() => onBulk('archive')} />
          {oneAccount && <MoveMenu account={oneAccount} onMove={(f) => onBulk('move', { folder_id: f.id })} />}
          <IconBtn label="Delete" icon={Trash2} danger onClick={() => onBulk('delete')} />
        </div>
      ) : (
        <div className="flex items-end gap-2 px-4 pt-3 pb-2">
          <div className="min-w-0 flex-1">
            <h2 className="text-[17px] font-semibold tracking-tight truncate flex items-center gap-1.5">
              {title}
              {onClearClient && <button type="button" onClick={onClearClient} aria-label="Show all mail" className="w-6 h-6 rounded flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-100"><X size={14} /></button>}
            </h2>
            <p className="text-[12px] text-slate-500 truncate">{subtitle}</p>
          </div>
          <div className="flex items-center gap-0.5 rounded-md bg-slate-100 p-0.5" role="group" aria-label="Show">
            {[['all', 'All'], ['unread', 'Unread'], ['flagged', 'Flagged']].map(([k, l]) => (
              <button key={k} type="button" onClick={() => setFilter(k)} aria-pressed={filter === k}
                className={clsx('px-2 h-7 rounded text-[12px] font-medium', filter === k ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900')}>{l}</button>
            ))}
          </div>
          <button type="button" onClick={onSync} disabled={syncing} title="Check for new mail" aria-label="Check for new mail"
            className="w-8 h-8 rounded-md flex items-center justify-center text-slate-500 hover:bg-slate-100 hover:text-slate-800 disabled:opacity-60">
            <RefreshCw size={15} className={syncing ? 'animate-spin' : ''} aria-hidden />
          </button>
        </div>
      )}
      {n === 0 && total > 0 && rows.length > 0 && (
        <div className="px-4 pb-1.5 -mt-0.5">
          <button type="button" onClick={() => setChecked(new Set(rows.map((r) => r.id)))} className="text-[11.5px] text-slate-500 hover:text-[#0f6cbd]">Select all {rows.length < total ? `${rows.length} shown` : rows.length}</button>
        </div>
      )}
    </div>
  );
}

function MessageList({ list, openId, onOpen, checked, setChecked, onAct, multi, accountOf, onMore, onRetry, term, prefs, tags, isPhone }) {
  const toggle = (id) => setChecked((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  if (list.loading && !list.rows.length) {
    return (
      <div className="flex-1 overflow-hidden" aria-busy="true" aria-label="Loading">
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className="flex gap-3 px-4 py-3 border-b border-slate-100">
            <div className="w-9 h-9 rounded-full bg-slate-100 animate-pulse" />
            <div className="flex-1 space-y-2"><div className="h-3 w-1/2 rounded bg-slate-100 animate-pulse" /><div className="h-3 w-4/5 rounded bg-slate-100 animate-pulse" /><div className="h-3 w-3/5 rounded bg-slate-50 animate-pulse" /></div>
          </div>
        ))}
      </div>
    );
  }
  if (list.error && !list.rows.length) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center text-center p-8">
        <AlertTriangle size={22} className="text-amber-600 mb-2" aria-hidden />
        <p className="text-[13.5px] text-slate-700">{list.error}</p>
        <button type="button" onClick={onRetry} className="mt-3 text-[13px] font-semibold text-[#0f6cbd] hover:underline">Try again</button>
      </div>
    );
  }
  if (!list.rows.length) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center text-center p-8">
        <div className="w-14 h-14 rounded-full bg-sky-50 flex items-center justify-center mb-3"><Inbox size={24} className="text-[#0f6cbd]" aria-hidden /></div>
        <p className="text-[14px] font-semibold text-slate-800">{term ? 'No mail matches that search' : 'All caught up'}</p>
        <p className="text-[12.5px] text-slate-500 mt-1 max-w-[240px]">{term ? 'Try a name, an email address or a word from the subject.' : 'Nothing here right now. New mail is checked every few minutes.'}</p>
      </div>
    );
  }
  let last = null;
  return (
    <div className="flex-1 min-h-0 overflow-y-auto mail-scroll" role="listbox" aria-label="Messages" aria-multiselectable="true">
      {list.rows.map((m) => {
        const g = groupOf(m.msg_date);
        const head = g !== last ? g : null;
        last = g;
        return (
          <React.Fragment key={m.id}>
            {head && <div className="sticky top-0 z-[1] px-4 py-1.5 text-[12px] font-semibold text-slate-600 bg-white/95 backdrop-blur-sm border-b border-slate-100">{head}</div>}
            <MessageRow m={m} active={m.id === openId} checked={checked.has(m.id)} anyChecked={checked.size > 0} onToggle={() => toggle(m.id)} onOpen={() => onOpen(m.id)}
              onAct={(a) => onAct([m.id], a)} account={multi ? accountOf(m.account_id) : null} canAct={!!(accountOf(m.account_id) || {}).can_act}
              prefs={prefs} tags={tags} isPhone={isPhone} />
          </React.Fragment>
        );
      })}
      {list.more && (
        <div className="p-3 text-center">
          <button type="button" onClick={onMore} className="text-[13px] font-semibold text-[#0f6cbd] hover:underline">Show more</button>
        </div>
      )}
    </div>
  );
}

const SWIPE_ACT = { archive: ['Archive', 'bg-emerald-600'], delete: ['Delete', 'bg-rose-600'], flag: ['Flag', 'bg-amber-500'], read: ['Read', 'bg-[#0f6cbd]'] };
function MessageRow({ m, active, checked, anyChecked, onToggle, onOpen, onAct, account, canAct, prefs, tags, isPhone }) {
  const outgoing = ['sent', 'drafts'].includes(m.folder_role);
  const person = outgoing ? firstOf(m.to_addr) : firstOf(m.from_addr);
  const name = outgoing ? `To: ${(m.to_addr || []).map(who).join(', ') || '(no one yet)'}` : who(person) || '(unknown sender)';
  const unread = !m.is_read;
  const ap = (prefs && prefs.appearance) || {};
  const compact = ap.density === 'compact';
  const swipeDo = (k) => { if (k === 'read') onAct(unread ? 'read' : 'unread'); else if (k === 'flag') onAct(m.is_flagged ? 'unflag' : 'flag'); else if (k && k !== 'none') onAct(k); };
  const sw = useSwipe({ enabled: (isPhone || TOUCH) && canAct, onLeft: () => swipeDo(prefs.swipe.left), onRight: () => swipeDo(prefs.swipe.right) });
  const side = sw.dx < 0 ? prefs.swipe.left : prefs.swipe.right;
  return (
    <div className="relative overflow-hidden">
    {sw.dx !== 0 && SWIPE_ACT[side] && (
      <div className={clsx('absolute inset-0 flex items-center px-5 text-white text-[13px] font-semibold', SWIPE_ACT[side][1], sw.dx < 0 ? 'justify-end' : 'justify-start')} aria-hidden>{SWIPE_ACT[side][0]}</div>
    )}
    <div role="option" aria-selected={active} tabIndex={-1} onClick={onOpen} {...sw.handlers} style={sw.dx ? { transform: `translateX(${sw.dx}px)` } : undefined}
      className={clsx('mail-row group relative flex gap-3 pl-4 pr-3 border-b border-slate-100 cursor-pointer select-none bg-white', compact ? 'py-1.5' : 'py-2.5',
        active ? '!bg-sky-100/70' : checked ? '!bg-sky-50' : 'hover:bg-slate-50')}>
      {unread && <span className="absolute left-0 top-0 bottom-0 w-[3px] bg-[#0f6cbd]" aria-hidden />}
      <button type="button" onClick={(e) => { e.stopPropagation(); onToggle(); }} aria-label={checked ? 'Unselect' : 'Select'} aria-pressed={checked}
        className={clsx('relative flex-shrink-0 rounded-full mt-0.5', compact ? 'w-7 h-7' : 'w-9 h-9', ap.avatars === false && !anyChecked && !checked && 'w-5')}>
        <span className={clsx('absolute inset-0 rounded-full text-white font-bold flex items-center justify-center transition-opacity', compact ? 'text-[10.5px]' : 'text-[12.5px]', (checked || anyChecked || ap.avatars === false) ? 'opacity-0' : 'group-hover:opacity-0')}
          style={{ background: avatarColor(person && person.address) }} aria-hidden>{initials(person && person.name, person && person.address)}</span>
        <span className={clsx('absolute inset-0 rounded-full flex items-center justify-center ring-1 transition-opacity',
          checked ? 'bg-[#0f6cbd] ring-[#0f6cbd] text-white opacity-100' : clsx('bg-white ring-slate-300 text-transparent', anyChecked ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'))} aria-hidden>
          <Check size={15} />
        </span>
      </button>
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2">
          <span className={clsx('truncate text-[13.5px]', unread ? 'font-semibold text-slate-900' : 'text-slate-800')}>{name}</span>
          {m.thread_count > 1 && <span className="flex-shrink-0 inline-flex items-center gap-0.5 text-[11px] text-slate-500" title={`${m.thread_count} messages in this conversation`}><MessagesSquare size={11} aria-hidden />{m.thread_count}</span>}
          <span className={clsx('ml-auto flex-shrink-0 text-[11.5px] tabular-nums group-hover:invisible', unread ? 'font-semibold text-[#0f6cbd]' : 'text-slate-500')}>{listTime(m.msg_date)}</span>
        </div>
        <div className="flex items-center gap-1.5 min-w-0">
          <span className={clsx('truncate text-[13px]', unread ? 'font-semibold text-[#0f6cbd]' : 'text-slate-700')}>{m.subject || '(no subject)'}</span>
          <span className="ml-auto flex items-center gap-1 flex-shrink-0 text-slate-400">
            {m.pinned_at && <Pin size={13} className="text-[#0f6cbd]" aria-label="Pinned" />}
            {m.snoozed_until && new Date(m.snoozed_until) > new Date() && <AlarmClockOff size={13} className="text-violet-600" aria-label={`Snoozed until ${fullTime(m.snoozed_until)}`} />}
            {m.note_count > 0 && <span className="inline-flex items-center gap-0.5 text-[11px] text-violet-700" title={`${m.note_count} internal note${m.note_count === 1 ? '' : 's'}`}><StickyNote size={12} aria-hidden />{m.note_count}</span>}
            {m.assignment && m.assignment.name && <span className={clsx('px-1.5 py-px rounded text-[10.5px] font-semibold', m.assignment.status === 'done' ? 'bg-emerald-50 text-emerald-700' : 'bg-sky-50 text-sky-800')} title={m.assignment.status === 'done' ? `Done (${m.assignment.name})` : `With ${m.assignment.name}`}>{m.assignment.status === 'done' ? '✓ ' : ''}{m.assignment.name.split(' ')[0]}</span>}
            {m.watch_due && <AlarmClock size={13} className={new Date(m.watch_due) <= new Date() ? 'text-rose-600' : 'text-amber-600'} aria-label="Reminder set" />}
            {m.has_invite && <CalendarPlus size={13} aria-label="Meeting invite" />}
            {m.has_attachments && <Paperclip size={13} aria-label="Has attachments" />}
            {m.is_flagged && <Flag size={13} className="text-rose-600 fill-rose-600" aria-label="Flagged" />}
          </span>
        </div>
        {ap.preview !== 0 && <p className={clsx('text-[12.5px] text-slate-500', ap.preview === 2 ? 'line-clamp-2' : 'truncate')}>{m.snippet || ' '}</p>}
        {((m.modules || []).length > 0 || account || (m.tag_ids || []).length > 0) && !compact && (
          <div className="flex items-center gap-1 mt-1 min-w-0">
            <TagChips ids={m.tag_ids} tags={tags} small />
            {(m.modules || []).map((k) => MODULES[k] && (
              <span key={k} className={clsx('px-1.5 py-px rounded text-[10.5px] font-semibold ring-1', MODULES[k].chip)}>{MODULES[k].short}</span>
            ))}
            {(m.links || []).slice(0, 1).map((l) => <span key={l.client_id} className="truncate text-[11px] text-slate-500">{l.label}</span>)}
            {account && <span className="ml-auto flex items-center gap-1 text-[10.5px] text-slate-400 min-w-0 max-w-[45%]"><span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: account.color || avatarColor(account.email) }} /><span className="truncate">{account.email}</span></span>}
          </div>
        )}
      </div>
      {/* Quick actions on hover, as in Outlook. */}
      {canAct && <div className="mail-row-actions absolute right-2 top-1.5 flex items-center gap-0.5 rounded-md bg-white/95 shadow-sm ring-1 ring-slate-200 p-0.5 opacity-0 transition-opacity" onClick={(e) => e.stopPropagation()}>
        <IconBtn small label="Delete" icon={Trash2} onClick={() => onAct('delete')} />
        <IconBtn small label="Archive" icon={Archive} onClick={() => onAct('archive')} />
        <IconBtn small label={m.is_flagged ? 'Unflag' : 'Flag'} icon={Flag} on={m.is_flagged} onClick={() => onAct(m.is_flagged ? 'unflag' : 'flag')} />
        <IconBtn small label={unread ? 'Mark as read' : 'Mark as unread'} icon={unread ? MailOpen : Mail} onClick={() => onAct(unread ? 'read' : 'unread')} />
      </div>}
    </div>
    </div>
  );
}

function IconBtn({ label, icon: Icon, onClick, danger, small, on, disabled }) {
  return (
    <button type="button" onClick={onClick} title={label} aria-label={label} disabled={disabled}
      className={clsx('rounded-md flex items-center justify-center transition-colors disabled:opacity-40', small ? 'w-7 h-7' : 'w-8 h-8',
        danger ? 'text-slate-600 hover:bg-rose-50 hover:text-rose-700' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900', on && 'text-rose-600')}>
      <Icon size={small ? 14 : 16} className={on ? 'fill-rose-600' : ''} aria-hidden />
    </button>
  );
}

function MoveMenu({ account, onMove }) {
  const folders = (account && account.folders) || [];
  return (
    <RMenu.Root>
      <RMenu.Trigger asChild>
        <button type="button" title="Move to" aria-label="Move to"
          className={clsx('rounded-md flex items-center justify-center gap-1.5 text-slate-600 hover:bg-slate-100 hover:text-slate-900 data-[state=open]:bg-slate-100', 'w-8 h-8')}>
          <FolderInput size={16} aria-hidden />
        </button>
      </RMenu.Trigger>
      <RMenu.Portal>
        <RMenu.Content align="start" sideOffset={4} className="z-[80] min-w-[220px] max-h-[360px] overflow-y-auto mail-scroll bg-white rounded-lg shadow-lg ring-1 ring-slate-200 p-1">
          <RMenu.Label className="px-2.5 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-500">Move to</RMenu.Label>
          {folders.map((f) => {
            const Icon = ROLE_ICON[f.role] || Folder;
            return (
              <RMenu.Item key={f.id} onSelect={() => onMove(f)} className="flex items-center gap-2.5 px-2.5 py-1.5 rounded-md text-[13px] text-slate-700 cursor-pointer outline-none data-[highlighted]:bg-slate-100">
                <Icon size={14} className="text-slate-500" aria-hidden /><span className="truncate">{f.path && f.path !== f.name ? f.path : f.name}</span>
              </RMenu.Item>
            );
          })}
        </RMenu.Content>
      </RMenu.Portal>
    </RMenu.Root>
  );
}

// ── Reading pane ────────────────────────────────────────────────────────────────────────────────
function NothingOpen({ count }) {
  return (
    <div className="flex-1 flex flex-col items-center justify-center text-center p-10 bg-[#fafafa]">
      <div className="w-16 h-16 rounded-2xl bg-white ring-1 ring-slate-200 shadow-sm flex items-center justify-center mb-4"><Mail size={26} className="text-[#0f6cbd]" aria-hidden /></div>
      <p className="text-[15px] font-semibold text-slate-800">Select a message to read it</p>
      <p className="text-[12.5px] text-slate-500 mt-1">{count ? `${count} in this view.` : ''} Press N for new mail, / to search, J and K to move.</p>
    </div>
  );
}

function ReadingPane({ detail, row, isPhone, onBack, onReply, onAct, account, onShowImages, onRetry, canCalendar, onFilterClient, onPrev, onNext,
  prefs, tags, thread, onOpenThread, aiPanel, onAi, onCloseAi, onUseAiReply, onTagged, onManageTags, onTask, onLogTime, onPrint, onBlock, onTrust, onWatched, canLogTime,
  ribbon, zoom, threadOpen, onToggleThread, canTask = true }) {
  const m = detail.data;
  const flagged = m ? m.is_flagged : row && row.is_flagged;
  const readOnly = !!(account && !account.can_act);
  const ON_RIBBON = ['archive', 'delete', 'move', 'junk', 'flag', 'unread', 'tag', 'print'];
  const tb = ((prefs && prefs.toolbar) || []).filter((k) => !ribbon || !ON_RIBBON.includes(k));
  const on = (k) => tb.includes(k);
  const primaryAll = prefs && prefs.replies.defaultReply === 'replyAll';
  const sc = m && (m.links || []).some((l) => l.module === 'sc');
  // Every action, in the person's order; those not on the toolbar go in the ⋯ menu.
  const acts = {
    reply: { label: 'Reply', icon: Reply, run: () => onReply('reply'), need: 'read' },
    replyAll: { label: 'Reply all', icon: ReplyAll, run: () => onReply('replyAll'), need: 'read' },
    forward: { label: 'Forward', icon: Forward, run: () => onReply('forward'), need: 'read' },
    archive: { label: 'Archive', icon: Archive, run: () => onAct('archive'), need: 'act' },
    delete: { label: 'Delete', icon: Trash2, run: () => onAct('delete'), need: 'act', danger: true },
    junk: { label: 'Move to Junk', icon: ShieldAlert, run: () => onAct('junk'), need: 'act' },
    flag: { label: flagged ? 'Unflag' : 'Flag', icon: Flag, run: () => onAct(flagged ? 'unflag' : 'flag'), need: 'act' },
    unread: { label: 'Mark as unread', icon: Mail, run: () => onAct('unread'), need: 'act' },
    task: { label: 'Create task', icon: ListTodo, run: onTask, need: 'read', hide: !canTask },
    logtime: { label: 'Log time / case note', icon: Clock4, run: onLogTime, need: 'read', hide: !canLogTime },
    translate: { label: 'Translate', icon: Languages, run: () => onAi('translate'), need: 'read', hide: !(prefs && prefs.ai.enabled) },
    print: { label: 'Print', icon: Printer, run: onPrint, need: 'read' },
  };
  const usable = (k) => acts[k] && !acts[k].hide && (acts[k].need !== 'act' || !readOnly) && m;
  // The person's own order. "Main reply button: Reply all" puts Reply all ahead of Reply.
  const bar = [...tb];
  const rIdx = bar.indexOf('reply'); const aIdx = bar.indexOf('replyAll');
  if (primaryAll && rIdx >= 0 && aIdx >= 0 && rIdx < aIdx) { bar[rIdx] = 'replyAll'; bar[aIdx] = 'reply'; }
  const tagToggle = async (t) => {
    const add = !(m.tag_ids || []).includes(t.id);
    try { await api.post('/mail/messages/tag', { ids: [m.id], tag_id: t.id, on: add }); onTagged(t.id, add); } catch (e) { toast.error(errMsg(e)); }
  };
  const moreKeys = Object.keys(acts).filter((k) => !on(k) && usable(k) && (!ribbon || !ON_RIBBON.includes(k)));
  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="flex-shrink-0 flex items-center gap-0.5 px-2 py-1.5 border-b border-slate-200 overflow-x-auto">
        {isPhone && <IconBtn label="Back" icon={ChevronLeft} onClick={onBack} />}
        {bar.map((k) => {
          if (k === 'move') return !readOnly && account && m ? <MoveMenu key={k} account={account} onMove={(f) => onAct('move', { folder_id: f.id })} /> : null;
          if (k === 'tag') return m ? <TagMenu key={k} ids={[m.id]} current={m.tag_ids || []} tags={tags} onChanged={onTagged} onManage={onManageTags} /> : null;
          if (k === 'remind') return m ? <RemindMenu key={k} messageId={m.id} sent={m.folder_role === 'sent'} onDone={onWatched} /> : null;
          if (k === 'ai') return m && prefs.ai.enabled ? <AiMenu key={k} onRun={onAi} /> : null;
          if (!usable(k)) return null;
          const a = acts[k];
          const wide = ['reply', 'replyAll', 'forward'].includes(k);
          if (k === 'flag') return <IconBtn key={k} label={a.label} icon={Flag} on={flagged} onClick={a.run} />;
          return <ToolBtn key={k} icon={a.icon} label={a.label} onClick={a.run} danger={a.danger} iconOnly={!wide} wide={wide} />;
        })}
        {m && (
          <MoreMenu items={[
            ...moreKeys.map((k) => ({ label: acts[k].label, icon: acts[k].icon, onClick: acts[k].run, danger: acts[k].danger })),
            // What is not on the toolbar is here, so nothing is ever out of reach.
            !on('remind') && { label: m.folder_role === 'sent' ? 'Watch for a reply' : 'Remind me', icon: AlarmClock, sub: remindItems(m.id, m.folder_role === 'sent', onWatched) },
            !on('ai') && prefs.ai.enabled && { label: 'AI assistant', icon: Sparkles, sub: aiItems(onAi) },
            !on('move') && !ribbon && !readOnly && account && { label: 'Move to', icon: FolderInput, sub: (account.folders || []).map((f) => ({ label: f.path && f.path !== f.name ? f.path : f.name, icon: ROLE_ICON[f.role] || Folder, onClick: () => onAct('move', { folder_id: f.id }) })) },
            !on('tag') && !ribbon && { label: 'Tag', icon: Tag, sub: [...(tags || []).map((t) => ({ label: t.name, dot: t.color, checked: (m.tag_ids || []).includes(t.id), onClick: () => tagToggle(t) })), (tags || []).length ? 'sep' : null, { label: 'Make and change tags', icon: Tag, onClick: onManageTags }] },
            !ribbon && 'sep',
            !ribbon && { label: 'Always show pictures from this sender', icon: ShieldCheck, onClick: onTrust },
            !readOnly && !ribbon && { label: 'Block this sender', icon: Ban, onClick: () => onBlock(false), danger: true },
            !readOnly && !ribbon && { label: 'Block everyone at this domain', icon: Ban, onClick: () => onBlock(true), danger: true },
          ]} />
        )}
        <span className="flex-1" />
        {!isPhone && <IconBtn label="Previous message" icon={ChevronLeft} onClick={onPrev} disabled={!onPrev} />}
        {!isPhone && <IconBtn label="Next message" icon={ChevronRight} onClick={onNext} disabled={!onNext} />}
      </div>
      {readOnly && (
        <p className="flex-shrink-0 px-4 py-1.5 bg-amber-50 border-b border-amber-100 text-[12px] text-amber-900">
          Read only: this is {account.owner_name ? `${account.owner_name}'s` : "someone else's"} mailbox. Opening mail here does not mark it read for them.
        </p>
      )}
      {detail.error ? (
        <div className="flex-1 flex flex-col items-center justify-center text-center p-8">
          <AlertTriangle size={22} className="text-amber-600 mb-2" aria-hidden />
          <p className="text-[13.5px] text-slate-700 max-w-sm">{detail.error}</p>
          <button type="button" onClick={onRetry} className="mt-3 text-[13px] font-semibold text-[#0f6cbd] hover:underline">Try again</button>
        </div>
      ) : !m ? (
        <div className="flex-1 p-6 space-y-4" aria-busy="true" aria-label="Opening">
          <div className="h-6 w-2/3 rounded bg-slate-100 animate-pulse" />
          <div className="flex gap-3"><div className="w-10 h-10 rounded-full bg-slate-100 animate-pulse" /><div className="flex-1 space-y-2"><div className="h-3 w-1/3 rounded bg-slate-100 animate-pulse" /><div className="h-3 w-1/4 rounded bg-slate-50 animate-pulse" /></div></div>
          <div className="space-y-2 pt-4">{[90, 80, 85, 60].map((w) => <div key={w} className="h-3 rounded bg-slate-50 animate-pulse" style={{ width: `${w}%` }} />)}</div>
        </div>
      ) : (
        <Message m={m} loadingImages={detail.loading} onShowImages={onShowImages} canCalendar={canCalendar} onFilterClient={onFilterClient}
          tags={tags} thread={thread} onOpenThread={onOpenThread} aiPanel={aiPanel} onCloseAi={onCloseAi} onUseAiReply={onUseAiReply} onTrust={onTrust}
          confirmLinks={prefs && prefs.read.confirmLinks} sc={sc} zoom={zoom} threadOpen={threadOpen} onToggleThread={onToggleThread} onRetry={onRetry}
          shared={!!(account && account.visibility !== 'owner')} onTeamChanged={onWatched} />
      )}
    </div>
  );
}
function ToolBtn({ icon: Icon, label, onClick, disabled, danger, iconOnly, wide }) {
  return (
    <button type="button" onClick={onClick} disabled={disabled} title={label} aria-label={label}
      className={clsx('flex-shrink-0 inline-flex items-center gap-1.5 h-8 rounded-md text-[13px] transition-colors disabled:opacity-40', iconOnly ? 'w-8 justify-center' : 'px-2.5',
        danger ? 'text-slate-700 hover:bg-rose-50 hover:text-rose-700' : 'text-slate-700 hover:bg-slate-100')}>
      <Icon size={16} className={danger ? '' : 'text-[#0f6cbd]'} aria-hidden />{!iconOnly && <span className={wide ? 'hidden sm:inline' : 'hidden'}>{label}</span>}
    </button>
  );
}

function Message({ m, loadingImages, onShowImages, canCalendar, onFilterClient, tags, thread, onOpenThread, aiPanel, onCloseAi, onUseAiReply, onTrust, confirmLinks, zoom = 100, threadOpen, onToggleThread, onRetry, shared, onTeamChanged }) {
  const from = firstOf(m.from) || {};
  const [showAll, setShowAll] = useState(false);
  const [linking, setLinking] = useState(false);
  const { user: me } = useAuth();
  // Admins can take any email off a file; others only ones linked by hand (the server checks whose).
  const canUnlink = (l) => (me && ['admin', 'super_admin'].includes(me.role)) || l.role === 'linked by hand';
  // Putting an email on a participant's file (or taking it off) by hand. A sync never undoes it.
  const changed = () => { if (onRetry) onRetry(); if (onTeamChanged) onTeamChanged(); };
  const link = async (t) => {
    if (!t) return;
    try { await api.post(`/mail/messages/${m.id}/links`, { client_id: t.client_id, module: t.module }); toast.success(`On ${t.name}'s file`); setLinking(false); changed(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const unlink = async (l) => {
    if (!window.confirm(`Take this email off ${l.label}'s file? It stays in the mailbox.`)) return;
    try { await api.delete(`/mail/messages/${m.id}/links/${l.client_id}`); toast.success(`Taken off ${l.label}'s file`); changed(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const recipients = (label, xs) => (xs && xs.length ? (
    <p className="text-[12.5px] text-slate-600 truncate"><span className="text-slate-500">{label}: </span>{xs.map((a) => a.name || a.address).join('; ')}</p>
  ) : null);
  const links = m.links || [];
  return (
    <div className="flex-1 min-h-0 overflow-y-auto mail-scroll">
      <div style={zoom !== 100 ? { zoom: zoom / 100 } : undefined}>
      <div className="px-5 sm:px-6 pt-5">
        <h1 className="text-[19px] leading-snug font-semibold tracking-tight text-slate-900 break-words">{m.subject || '(no subject)'}</h1>
        {shared && <TeamPanel messageId={m.id} onChanged={onTeamChanged} />}
        {(m.tag_ids || []).length > 0 && <div className="flex flex-wrap gap-1.5 mt-2"><TagChips ids={m.tag_ids} tags={tags} /></div>}
        {canCalendar && (
          <div className="flex flex-wrap gap-1.5 mt-2.5">
            {links.slice(0, showAll ? links.length : 4).map((l) => {
              const M = MODULES[l.module] || MODULES.care;
              return (
                <span key={`${l.client_id}-${l.module}`} className={clsx('inline-flex items-center rounded-full ring-1 text-[12px] font-medium overflow-hidden', M.chip)}>
                  <Link to={fileHref(l)} className="inline-flex items-center gap-1.5 pl-2 pr-1.5 py-0.5 hover:underline" title={`Open ${l.label}'s file (${l.role})`}>
                    <M.icon size={12} aria-hidden />{l.label}<span className="opacity-70">· {M.short}</span>
                  </Link>
                  <button type="button" onClick={() => onFilterClient(l)} className="px-1.5 py-0.5 border-l border-current/20 opacity-70 hover:opacity-100" title={`All mail with ${l.label}`} aria-label={`All mail with ${l.label}`}><Search size={11} /></button>
                  {canUnlink(l) && <button type="button" onClick={() => unlink(l)} className="px-1.5 py-0.5 border-l border-current/20 opacity-70 hover:opacity-100" title={`Take off ${l.label}'s file`} aria-label={`Take off ${l.label}'s file`}><X size={11} /></button>}
                </span>
              );
            })}
            {links.length > 4 && !showAll && <button type="button" onClick={() => setShowAll(true)} className="text-[12px] text-slate-500 hover:text-slate-800">+{links.length - 4} more</button>}
            <button type="button" onClick={() => setLinking(true)}
              className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[12px] font-medium text-slate-600 ring-1 ring-dashed ring-slate-300 hover:bg-slate-50 hover:text-slate-900">
              <Link2 size={12} aria-hidden />{links.length ? 'Link to another' : 'Link to a participant'}
            </button>
          </div>
        )}
        {!canCalendar && links.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-2.5">
            {links.map((l) => <span key={`${l.client_id}-${l.module}`} className="text-[12px] text-slate-600">{l.label}</span>)}
          </div>
        )}
        <Modal open={linking} onClose={() => setLinking(false)} title="Link to a participant"
          sub="The email goes on their file and is kept there for your company's retention period, even if it is deleted from the mailbox.">
          <div className="min-h-[220px]"><LinkPicker value={null} onChange={link} /></div>
        </Modal>
        <div className="flex gap-3 mt-4">
          <span className="w-10 h-10 rounded-full text-white text-[14px] font-bold flex items-center justify-center flex-shrink-0" style={{ background: avatarColor(from.address) }} aria-hidden>{initials(from.name, from.address)}</span>
          <div className="min-w-0 flex-1">
            <div className="flex items-baseline gap-2 flex-wrap">
              <span className="text-[14px] font-semibold text-slate-900">{from.name || from.address}</span>
              {from.name && <span className="text-[12.5px] text-slate-500 truncate">&lt;{from.address}&gt;</span>}
              <span className="ml-auto text-[12px] text-slate-500 whitespace-nowrap">{fullTime(m.date)}</span>
            </div>
            {recipients('To', m.to)}
            {recipients('Cc', m.cc)}
          </div>
        </div>
        {m.body_error && (
          <div className="mt-4 flex items-center gap-2.5 rounded-lg bg-amber-50 ring-1 ring-amber-200 px-3 py-2 text-[12.5px] text-amber-900" role="status">
            <AlertTriangle size={15} className="flex-shrink-0" aria-hidden />
            <span className="flex-1">{m.body_error}</span>
            {onRetry && <button type="button" onClick={onRetry} className="font-semibold text-[#0f6cbd] hover:underline whitespace-nowrap">Try again</button>}
          </div>
        )}
        {m.blocked_images > 0 && (
          <div className="mt-4 flex items-center gap-2.5 rounded-lg bg-slate-50 ring-1 ring-slate-200/80 px-3 py-2 text-[12.5px] text-slate-700">
            <ImageIcon size={15} className="text-slate-500 flex-shrink-0" aria-hidden />
            <span className="flex-1">Pictures in this message were blocked so the sender cannot tell when it was opened.</span>
            <span className="flex flex-wrap gap-x-3 gap-y-1 justify-end">
              <button type="button" onClick={onShowImages} disabled={loadingImages} className="font-semibold text-[#0f6cbd] hover:underline whitespace-nowrap disabled:opacity-60">
                {loadingImages ? 'Loading…' : 'Show pictures'}
              </button>
              <button type="button" onClick={onTrust} className="font-semibold text-[#0f6cbd] hover:underline whitespace-nowrap">Always from this sender</button>
            </span>
          </div>
        )}
        {m.trackers > 0 && (
          <p className="mt-2 flex items-center gap-1.5 text-[12px] text-emerald-700"><ShieldCheck size={13} aria-hidden /> {m.trackers} tracker{m.trackers === 1 ? '' : 's'} blocked</p>
        )}
        <AiPanel panel={aiPanel} onClose={onCloseAi} onUseReply={onUseAiReply} />
        <ThreadHeader thread={thread} openId={m.id} onOpen={onOpenThread} open={onToggleThread ? threadOpen : undefined} onToggle={onToggleThread} />
        {m.invite && <InviteCard invite={m.invite} canCalendar={canCalendar} links={links} />}
        {(m.attachments || []).filter((a) => !/calendar|\.ics$/i.test(`${a.content_type} ${a.filename}`) || !m.invite).length > 0 && (
          <Attachments id={m.id} items={(m.attachments || []).filter((a) => !/calendar|\.ics$/i.test(`${a.content_type} ${a.filename}`) || !m.invite)} />
        )}
      </div>
      <Body html={m.html} text={m.text} confirmLinks={confirmLinks} />
      </div>
    </div>
  );
}

const PREVIEW = ['image/png', 'image/jpeg', 'image/gif', 'image/webp', 'application/pdf'];
function Attachments({ id, items }) {
  const [busy, setBusy] = useState(null);
  const get = async (a, view) => {
    setBusy(a.id);
    try {
      const type = String(a.content_type || '').toLowerCase().split(';')[0].trim();
      const r = await api.get(`/mail/messages/${id}/attachments/${encodeURIComponent(a.id)}`, { params: view ? { inline: 1 } : {}, responseType: 'blob' });
      // Only pictures and PDFs open here, typed by their own kind; anything else is saved.
      const blob = view && PREVIEW.includes(type) ? new Blob([r.data], { type }) : new Blob([r.data], { type: 'application/octet-stream' });
      const href = URL.createObjectURL(blob);
      if (view && PREVIEW.includes(type)) window.open(href, '_blank', 'noopener');
      else { const el = document.createElement('a'); el.href = href; el.download = a.filename || 'attachment'; document.body.appendChild(el); el.click(); el.remove(); }
      setTimeout(() => URL.revokeObjectURL(href), 60000);
    } catch (e) {
      let msg = 'Could not get that attachment.';
      try { const t = e.response && e.response.data && await e.response.data.text(); msg = (t && JSON.parse(t).message) || msg; } catch { /* not JSON */ }
      toast.error(msg);
    } finally { setBusy(null); }
  };
  return (
    <div className="mt-4 flex flex-wrap gap-2">
      {items.map((a) => {
        const type = String(a.content_type || '').toLowerCase().split(';')[0].trim();
        const viewable = PREVIEW.includes(type);
        const ext = (String(a.filename || '').split('.').pop() || '').slice(0, 4).toUpperCase();
        return (
          <div key={a.id} className="group flex items-center gap-2.5 rounded-lg ring-1 ring-slate-200 bg-white pl-2 pr-1 py-1.5 max-w-[280px] hover:ring-slate-300">
            <span className={clsx('w-8 h-8 rounded-md flex items-center justify-center text-[9.5px] font-bold flex-shrink-0', type === 'application/pdf' ? 'bg-rose-50 text-rose-700' : type.startsWith('image/') ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-600')} aria-hidden>{ext || 'FILE'}</span>
            <button type="button" onClick={() => get(a, viewable)} className="min-w-0 text-left">
              <span className="block text-[12.5px] font-medium text-slate-800 truncate">{a.filename}</span>
              <span className="block text-[11px] text-slate-500">{fmtSize(a.size) || (viewable ? 'Click to open' : 'Click to save')}</span>
            </button>
            <span className="flex items-center">
              {busy === a.id ? <Loader2 size={14} className="animate-spin text-slate-400 mx-2" aria-hidden /> : (
                <>
                  {viewable && <IconBtn small label={`Open ${a.filename}`} icon={Eye} onClick={() => get(a, true)} />}
                  <IconBtn small label={`Save ${a.filename}`} icon={Download} onClick={() => get(a, false)} />
                </>
              )}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function InviteCard({ invite, canCalendar, links }) {
  const t = inviteTimes(invite);
  const [added, setAdded] = useState(null);
  const [busy, setBusy] = useState(false);
  const cancelled = invite.method === 'CANCEL';
  const sc = links.find((l) => l.module === 'sc' && l.coordination_id);
  const other = !sc && links.find((l) => l.module === 'pm' || l.module === 'care');
  const add = async () => {
    setBusy(true);
    try {
      const body = {
        title: (invite.summary || 'Meeting').slice(0, 200), kind: 'meeting', all_day: t.all_day, start_at: t.start.toISOString(), end_at: t.end.toISOString(),
        tz: Intl.DateTimeFormat().resolvedOptions().timeZone || 'Australia/Sydney', location: (invite.location || '').slice(0, 300) || null,
        notes: [invite.organizer ? `Invite from ${invite.organizer}` : '', (invite.description || '').slice(0, 3500)].filter(Boolean).join('\n\n') || null,
      };
      if (sc) Object.assign(body, { coordination_id: sc.coordination_id, module: 'sc' });
      else if (other) Object.assign(body, { module: other.module, client_id: other.client_id });
      await api.post('/calendar/appointments', body);
      setAdded(t.start);
      toast.success('Added to your calendar');
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return (
    <div className={clsx('mt-4 rounded-xl ring-1 overflow-hidden', cancelled ? 'ring-rose-200 bg-rose-50/40' : 'ring-sky-200 bg-sky-50/40')}>
      <div className="flex flex-wrap gap-3 p-3.5">
        <div className={clsx('w-12 flex-shrink-0 rounded-lg bg-white ring-1 text-center overflow-hidden', cancelled ? 'ring-rose-200' : 'ring-sky-200')} aria-hidden>
          <div className={clsx('text-[10px] font-bold uppercase text-white py-0.5', cancelled ? 'bg-rose-600' : 'bg-[#0f6cbd]')}>{t ? t.start.toLocaleDateString('en-AU', { month: 'short' }) : '—'}</div>
          <div className="text-[18px] font-semibold text-slate-900 py-0.5">{t ? t.start.getDate() : '?'}</div>
        </div>
        <div className="min-w-[160px] flex-1">
          <p className="text-[14px] font-semibold text-slate-900">{cancelled && <span className="text-rose-700">Cancelled: </span>}{invite.summary || 'Meeting'}</p>
          {t && <p className="text-[12.5px] text-slate-700 mt-0.5">{inviteWhen(t)}</p>}
          {invite.location && <p className="text-[12.5px] text-slate-600 mt-0.5 flex items-center gap-1"><MapPin size={12} aria-hidden />{invite.location}</p>}
          {invite.organizer && <p className="text-[12px] text-slate-500 mt-0.5">Organiser: {invite.organizer}</p>}
        </div>
        {canCalendar && t && !cancelled && (
          <div className="basis-full sm:basis-auto flex-shrink-0 self-center pl-[60px] sm:pl-0">
            {added ? (
              <Link to={`/mail?view=calendar&cv=day&d=${ymd(added)}`} className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md bg-white ring-1 ring-emerald-200 text-emerald-700 text-[12.5px] font-semibold hover:bg-emerald-50">
                <CalendarCheck size={14} aria-hidden /> In your calendar
              </Link>
            ) : (
              <button type="button" onClick={add} disabled={busy}
                className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md bg-[#0f6cbd] text-white text-[12.5px] font-semibold hover:bg-[#115ea3] disabled:opacity-60">
                {busy ? <Loader2 size={14} className="animate-spin" aria-hidden /> : <CalendarPlus size={14} aria-hidden />} Add to my calendar
              </button>
            )}
          </div>
        )}
      </div>
      {canCalendar && (sc || other) && !added && !cancelled && <p className="px-3.5 pb-2.5 -mt-1 text-[11.5px] text-slate-500">It will be linked to {(sc || other).label}'s file.</p>}
    </div>
  );
}

// ── Nothing connected yet ───────────────────────────────────────────────────────────────────────
function Welcome({ onAdd, status }) {
  return (
    <div className="flex-1 overflow-y-auto mail-scroll bg-gradient-to-b from-sky-50/70 to-white">
      <div className="max-w-2xl mx-auto px-6 py-12 sm:py-16">
        <div className="w-14 h-14 rounded-2xl bg-[#0f6cbd] text-white flex items-center justify-center shadow-lg shadow-sky-900/10"><Mail size={26} aria-hidden /></div>
        <h1 className="mt-6 text-[26px] sm:text-[30px] font-semibold tracking-tight text-slate-900">Your email, inside Rostery</h1>
        <p className="mt-2 text-[15px] text-slate-600 leading-relaxed max-w-xl">
          Sign in with Microsoft or Google, or add any other mailbox. Read, reply and file mail here, and see straight away which participant it is about.
        </p>
        <div className="mt-7 flex flex-wrap gap-3">
          <button type="button" onClick={onAdd} className="inline-flex items-center gap-2 h-11 px-5 rounded-lg bg-[#0f6cbd] text-white text-[14px] font-semibold shadow-sm hover:bg-[#115ea3]">
            <Plus size={17} aria-hidden /> Add a mailbox
          </button>
          {status && !status.microsoft && <p className="self-center text-[12.5px] text-slate-500">Microsoft sign-in needs an admin to add the app details first (Plan Management › Settings › Email & Mailbox).</p>}
        </div>
        <div className="mt-10 grid sm:grid-cols-3 gap-4">
          {[
            [HeartHandshake, 'Tagged by participant', 'Mail with a participant, their nominee or a provider is marked SC, PM or Client and links to their file.'],
            [CalendarPlus, 'Invites to your calendar', 'A meeting invite shows as a card. One click puts it in your Support Coordination calendar.'],
            [ShieldAlert, 'Private by default', 'Pictures stay blocked until you allow them. You choose who else in the office can see each mailbox.'],
          ].map(([Icon, h, p]) => (
            <div key={h} className="rounded-xl bg-white ring-1 ring-slate-200/80 p-4">
              <Icon size={18} className="text-[#0f6cbd]" aria-hidden />
              <p className="mt-2.5 text-[13.5px] font-semibold text-slate-900">{h}</p>
              <p className="mt-1 text-[12.5px] text-slate-600 leading-relaxed">{p}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
